import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import AdminDashboardLayout from "../../layouts/AdminDashboardLayout";
import BrandLoader from "../../components/BrandLoader";
import {
  FiPlus, FiEdit3, FiTrash2, FiX, FiInfo,
  FiCheckCircle, FiTrendingUp, FiDownload,
  FiClock, FiUsers, FiCopy, FiCalendar,
  FiChevronDown, FiZap, FiLayers, FiEye,
  FiAlertCircle, FiSliders, FiSettings, FiCheck,
  FiDroplet, FiRefreshCw, FiActivity, FiGlobe, FiAlertTriangle, FiSearch,
  FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight
} from "react-icons/fi";
import { dietPlanAPI } from "../../services/api";
import { useDynamicPackages } from "../../hooks/useDynamicPackages";

// ─── TOKENS ──────────────────────────────────────────────────────────────────
const T = {
  olive:"#6B8A5A", olive2:"#8BA47A",
  orange:"#F29A0C", orange2:"#E97818",
  dark:"#1F1F1F", mid:"#555", muted:"#AAA", light:"#CCC",
  border:"rgba(239,229,216,0.8)",
  green:"#22C55E", greenDk:"#16A34A",
  red:"#E24B4A", amber:"#F59E0B", blue:"#185FA5",
};

// ─── EXPORT HELPER ───────────────────────────────────────────────────────────
const exportPlanAsFile = (plan) => {
  if (!plan) return;
  let text = `===================================================\n`;
  text += `DIVYYOG WELLNESS - 7-DAY DIET PLAN REPORT\n`;
  text += `===================================================\n\n`;
  text += `Plan Name: ${plan.name}\n`;
  text += `Target Tier: ${plan.tier}\n`;
  text += `Dietary Category: ${plan.dietType}\n`;
  text += `Region / Cuisine: ${plan.region}\n`;
  text += `Caloric Goal: ${plan.calorieGoal}\n`;
  text += `Daily Target Calories: ${plan.totalCal || plan.totalCalories} kcal\n`;
  text += `Status: ${plan.status}\n\n`;
  text += `---------------------------------------------------\n`;
  text += `WEEKLY MEAL SCHEDULE\n`;
  text += `---------------------------------------------------\n\n`;

  const DAYS_LIST = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];

  if (plan.days && plan.days.length > 0) {
    plan.days.forEach(d => {
      text += `=== ${d.day.toUpperCase()} ===\n`;
      d.meals.forEach(m => {
        text += `• [${m.time || '08:00 AM'}] ${m.slot || m.mealSlot || 'Meal'}: ${m.name || m.mealName} (${m.cal || m.calories} kcal | P:${m.protein}g C:${m.carbs}g F:${m.fat}g)\n`;
      });
      text += `\n`;
    });
  } else if (plan.rawMeals) {
    DAYS_LIST.forEach(day => {
      text += `=== ${day.toUpperCase()} ===\n`;
      const dayMeals = plan.rawMeals.filter(m => m.day === day);
      dayMeals.forEach(m => {
        text += `• [${m.time || '08:00 AM'}] ${m.mealSlot}: ${m.mealName} (${m.calories} kcal | P:${m.protein}g C:${m.carbs}g F:${m.fat}g)\n`;
      });
      text += `\n`;
    });
  }

  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${(plan.name || 'DietPlan').replace(/\s+/g, '_')}_7Day_Plan.txt`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const exportTrackingDataAsCSV = (data) => {
  if (!data || data.length === 0) return;
  
  const headers = ["Name", "Email", "Tier", "Preferences", "Assigned Plan", "Weekly Progress", "Today's Progress", "Water Intake"];
  
  const rows = data.map(item => {
    const name = item.fullName || "";
    const email = item.email || "";
    const tier = item.tier || "";
    const preferences = `${item.dietType || ""} | ${item.region || ""} | ${item.calorieGoal || ""}`;
    const assignedPlan = item.matchedPlanName || "No Published Plan";
    const weeklyProgress = `${item.completedMeals || 0} / ${item.totalMeals || 0} meals`;
    
    let todaysProgress = "";
    if (item.totalMealsToday === 0) {
      todaysProgress = "No meals today";
    } else {
      todaysProgress = `${item.completedMealsToday || 0} / ${item.totalMealsToday || 0} meals`;
    }
    
    const waterIntake = `${item.waterIntakeGss === 4 ? 0 : (item.waterIntakeGss || 0)} / 12 glasses`;
    
    return [name, email, tier, preferences, assignedPlan, weeklyProgress, todaysProgress, waterIntake].map(val => {
      const escaped = String(val).replace(/"/g, '""');
      return `"${escaped}"`;
    });
  });
  
  const csvContent = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `Diet_Tracking_Report_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// ─── CUSTOM CONFIRMATION MODAL (NO ALERT OR DEFAULT PROMPT) ──────────────────
const ConfirmModal = ({ isOpen, title, message, confirmText="Confirm", cancelText="Cancel", isDanger=false, onConfirm, onCancel }) => {
  if (!isOpen) return null;
  return (
    <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
      className="fixed inset-0 z-[400] flex items-center justify-center p-4"
      style={{ background:"rgba(15,15,15,0.45)", backdropFilter:"blur(6px)" }}
      onClick={onCancel}>
      <motion.div initial={{ scale:0.92, opacity:0 }} animate={{ scale:1, opacity:1 }}
        exit={{ scale:0.92, opacity:0 }} onClick={e => e.stopPropagation()}
        className="rounded-[24px] p-6 max-w-sm w-full text-center relative border"
        style={{ background:"rgba(255,253,248,0.98)", borderColor:"rgba(255,255,255,0.9)", boxShadow:"0 25px 60px rgba(0,0,0,0.2)" }}>
        <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
          style={{ background: isDanger ? "rgba(226,75,74,0.12)" : "rgba(107,138,90,0.12)" }}>
          {isDanger ? <FiTrash2 size={20} style={{ color: T.red }}/> : <FiInfo size={20} style={{ color: T.olive }}/>}
        </div>
        <h3 className="font-black text-[16px] text-[#1F1F1F]" style={{ fontFamily: "'Poppins',sans-serif" }}>{title}</h3>
        <p className="text-[12px] mt-1.5 mb-5 text-[#666] leading-relaxed">{message}</p>
        <div className="flex gap-2">
          <button onClick={onCancel} className="flex-1 py-2.5 rounded-full text-[12px] font-bold border cursor-pointer"
            style={{ background: "#fff", borderColor: T.border, color: T.mid }}>
            {cancelText}
          </button>
          <button onClick={onConfirm} className="flex-1 py-2.5 rounded-full text-[12px] font-bold text-white border-none cursor-pointer"
            style={{ background: isDanger ? T.red : `linear-gradient(135deg,${T.olive},${T.olive2})` }}>
            {confirmText}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ─── CUSTOM DROPDOWN SELECTOR WITH SEARCH (NO DEFAULT HTML SELECT) ────────────
const CustomSelect = ({ value, onChange, options, placeholder="Select...", label, hasError = false, searchable = true }) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedObj = options.find(o => (typeof o === "string" ? o : o.value) === value);
  const displayLabel = typeof selectedObj === "string" ? selectedObj : selectedObj?.label || selectedObj?.value || value;

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;
    const term = searchTerm.toLowerCase();
    return options.filter(opt => {
      const val = typeof opt === "string" ? opt : String(opt.value || "");
      const lbl = typeof opt === "string" ? opt : String(opt.label || opt.value || "");
      return val.toLowerCase().includes(term) || lbl.toLowerCase().includes(term);
    });
  }, [options, searchTerm]);

  return (
    <div className="relative w-full" ref={ref}>
      {label && <label className="text-[10px] font-black uppercase tracking-[0.9px] block mb-1.5" style={{ color: T.muted }}>{label}</label>}
      <div 
        onClick={() => setOpen(!open)}
        className="w-full border rounded-[12px] px-3.5 py-2 text-[12px] font-semibold flex items-center justify-between cursor-pointer transition-all"
        style={{
          borderColor: hasError ? "#EF4444" : open ? T.olive : T.border,
          background: hasError ? "rgba(254,242,242,0.6)" : "rgba(255,255,255,0.95)",
          color: value ? T.dark : "#888",
          boxShadow: open ? "0 4px 14px rgba(107,138,90,0.15)" : "none"
        }}
      >
        <span className="truncate">{value ? displayLabel : placeholder}</span>
        <FiChevronDown size={14} style={{ color: open ? T.olive : T.muted, transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}/>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div 
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 4, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 right-0 z-[300] max-h-60 overflow-hidden flex flex-col rounded-[16px] p-1.5 shadow-2xl border"
            style={{ background: "#FFFDF8", borderColor: "rgba(239,229,216,0.9)" }}
          >
            {/* Search Input inside Dropdown Popover */}
            {searchable && options.length > 3 && (
              <div className="p-1 mb-1 border-b border-zinc-100/90 sticky top-0 bg-[#FFFDF8] z-10">
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-zinc-100/90 border border-zinc-200/70 focus-within:border-[#6B8A5A] transition-all">
                  <FiSearch size={13} className="text-zinc-400 shrink-0" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search options..."
                    className="w-full text-[11.5px] bg-transparent outline-none text-zinc-800 placeholder:text-zinc-400 font-medium"
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setSearchTerm(""); }}
                      className="text-zinc-400 hover:text-zinc-600 bg-transparent border-none cursor-pointer p-0 flex items-center justify-center"
                    >
                      <FiX size={12} />
                    </button>
                  )}
                </div>
              </div>
            )}

            <div className="overflow-y-auto flex-1 max-h-48" style={{ scrollbarWidth: "none" }}>
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-3 text-center text-[11.5px] text-zinc-400 italic">
                  No matching options found
                </div>
              ) : (
                filteredOptions.map((opt) => {
                  const val = typeof opt === "string" ? opt : opt.value;
                  const lbl = typeof opt === "string" ? opt : opt.label;
                  const isSelected = val === value;
                  return (
                    <div
                      key={val}
                      onClick={() => {
                        onChange(val);
                        setOpen(false);
                        setSearchTerm("");
                      }}
                      className="px-3 py-2 text-[12px] font-medium rounded-[10px] cursor-pointer flex items-center justify-between transition-colors mb-0.5"
                      style={{
                        background: isSelected ? "rgba(107,138,90,0.12)" : "transparent",
                        color: isSelected ? T.olive : T.dark,
                        fontWeight: isSelected ? "700" : "500"
                      }}
                    >
                      <span className="truncate">{lbl}</span>
                      {isSelected && <FiCheckCircle size={13} style={{ color: T.olive }} className="shrink-0 ml-1" />}
                    </div>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const TIERS = ["Free","Basic","Standard","Premium","YTT Pro"];
const TIER_S = {
  Free:     { bg:"rgba(200,200,200,0.12)", c:"#666",    b:"rgba(200,200,200,0.3)",  grad:"linear-gradient(135deg,#AAA,#CCC)" },
  Basic:    { bg:"rgba(24,95,165,0.10)",   c:"#0C447C", b:"rgba(24,95,165,0.20)",   grad:"linear-gradient(135deg,#185FA5,#378ADD)" },
  Standard: { bg:"rgba(83,74,183,0.10)",   c:"#3C3489", b:"rgba(83,74,183,0.20)",   grad:"linear-gradient(135deg,#534AB7,#7F77DD)" },
  Premium:  { bg:"rgba(242,154,12,0.12)",  c:"#C8660A", b:"rgba(242,154,12,0.25)",  grad:"linear-gradient(135deg,#F29A0C,#E97818)" },
  "YTT Pro":{ bg:"rgba(107,138,90,0.12)",  c:"#27500A", b:"rgba(107,138,90,0.25)",  grad:"linear-gradient(135deg,#6B8A5A,#8BA47A)" },
};

const STATUS_S = {
  Published: { bg:"rgba(34,197,94,0.1)",  c:"#16A34A", dot:"#22C55E" },
  Draft:     { bg:"rgba(245,158,11,0.1)", c:"#B45309", dot:"#F59E0B" },
  Archived:  { bg:"rgba(156,163,175,0.1)",c:"#666",    dot:"#9CA3AF" },
};

const DAYS  = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];
const MEALS = ["Breakfast","Lunch","Dinner","Snack"];



// ─── DB PLAN FORMATTER ────────────────────────────────────────────────────────
function formatPlanFromDb(p) {
  const days = DAYS.map((dayName) => {
    const dayMeals = (p.meals || []).filter(m => m.day === dayName);
    const slots = MEALS.map(slotName => {
      const found = dayMeals.find(m => m.mealSlot === slotName);
      if (found) {
        return {
          id: found.id,
          name: found.mealName,
          description: found.description || found.Description || found.notes || found.Notes || "",
          cal: found.calories,
          protein: found.protein,
          carbs: found.carbs,
          fat: found.fat,
          prepTime: found.prepTime || "15 min",
          notes: found.description || found.Description || found.notes || found.Notes || "",
          tags: found.tags ? found.tags.split(",") : ["Healthy"]
        };
      }
      return {
        name: `${slotName} Balanced Option`,
        description: "Nutritionist recipe & meal guidance.",
        cal: 300,
        protein: 15,
        carbs: 35,
        fat: 8,
        prepTime: "15 min",
        notes: "Nutritionist recipe.",
        tags: ["Healthy"]
      };
    });
    return { day: dayName, meals: slots };
  });

  return {
    id: p.id,
    name: p.name,
    tier: p.tier,
    status: p.status || "Published",
    description: p.description || p.Description || "",
    week: "7-Day Menu",
    region: p.region || "Indian",
    dietType: p.dietType || "Veg",
    calorieGoal: p.calorieGoal || "Balanced",
    totalCal: p.totalCalories || p.totalCal || 1800,
    subscribers: p.subscribers || 0,
    version: "v1.0",
    autoPublish: true,
    days: days,
    rawMeals: p.meals || []
  };
}

// ─── SMALL COMPONENTS ─────────────────────────────────────────────────────────
const TierPill = ({ tier }) => {
  const s = TIER_S[tier] || TIER_S.Free;
  return (
    <span className="inline-flex items-center text-[10px] font-black px-2.5 py-1 rounded-full"
      style={{ background:s.bg, color:s.c, border:`1px solid ${s.b}` }}>{tier}</span>
  );
};

const StatusPill = ({ status }) => {
  const s = STATUS_S[status] || STATUS_S.Draft;
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full"
      style={{ background:s.bg, color:s.c }}>
      <span className="w-[5px] h-[5px] rounded-full flex-shrink-0" style={{ background:s.dot }}/>
      {status}
    </span>
  );
};

const MacroBadge = ({ label, value, color }) => (
  <div className="text-center">
    <p className="font-black text-[13px] leading-none" style={{ fontFamily:"'Poppins',sans-serif", color }}>{value}g</p>
    <p className="text-[9px] font-bold uppercase tracking-[0.5px] mt-0.5" style={{ color:T.muted }}>{label}</p>
  </div>
);

const StatCard = ({ icon:Icon, value, label, trend, iconBg, accentColor, cardBg, delay=0 }) => (
  <motion.div initial={{ opacity:0,y:16 }} animate={{ opacity:1,y:0 }} transition={{ delay }}
    whileHover={{ y:-4, boxShadow:"0 22px 50px rgba(0,0,0,0.1)" }}
    className="ad-kpi-card ad-kpi-flat relative overflow-hidden rounded-[20px] p-4 border"
    style={{ background:cardBg||"rgba(255,255,255,0.85)", borderColor:"rgba(255,255,255,0.9)", transition:"all 0.28s" }}>
    <div className="ad-kpi-icon w-9 h-9 rounded-[11px] flex items-center justify-center mb-3" style={{ background:iconBg }}>
      <Icon size={16} style={{ color:"#fff" }}/>
    </div>
    <p className="ad-kpi-val font-black text-[#1F1F1F] mb-1"
      style={{ fontFamily:"'Poppins',sans-serif", fontSize:24, letterSpacing:"-1px", lineHeight:1 }}>{value}</p>
    <p className="ad-kpi-lbl text-[10px] font-bold uppercase tracking-[0.8px] mb-1.5" style={{ color:T.muted }}>{label}</p>
    <p className="ad-kpi-note text-[11px] font-bold flex items-center gap-1" style={{ color:T.greenDk }}>
      <FiTrendingUp size={11}/>{trend}
    </p>
    <div className="absolute bottom-0 left-0 right-0 h-[3px] rounded-b-[20px]" style={{ background:accentColor }}/>
  </motion.div>
);

// ─── PAGINATION SUITE ────────────────────────────────────────────────────────
const PaginationSuite = ({
  currentPage,
  totalPages,
  totalItems,
  startIndex,
  endIndex,
  pageSize,
  itemLabel = "items",
  onPageChange
}) => {
  const pageSizeNum = pageSize === "all" ? totalItems : Number(pageSize);
  const startNum = startIndex !== undefined ? startIndex : (totalItems > 0 ? (currentPage - 1) * (pageSize === "all" ? totalItems : pageSizeNum) + 1 : 0);
  const endNum = endIndex !== undefined ? endIndex : (pageSize === "all" ? totalItems : Math.min(currentPage * pageSizeNum, totalItems));

  return (
    <div
      className="flex flex-col sm:flex-row items-center justify-between gap-3 px-6 py-4 border-t mt-5 rounded-[20px]"
      style={{ borderColor: "rgba(239,229,216,0.6)", background: "rgba(255,255,255,0.45)" }}
    >
      <div className="text-[12px] font-bold text-zinc-500">
        Showing <span className="text-zinc-900">{startNum}</span> to{" "}
        <span className="text-zinc-900">{endNum}</span> of{" "}
        <span className="text-zinc-900">{totalItems}</span> {itemLabel}
      </div>

      {totalPages > 1 && (
        <div className="flex flex-wrap items-center gap-2">
          {/* Active Page Counter Pill */}
          <span className="text-[11.5px] font-black text-amber-900 bg-amber-100/90 px-3 py-1 rounded-full border border-amber-300 shadow-2xs mr-1">
            Page {currentPage} of {totalPages}
          </span>

          {/* Jump to First Page Button */}
          <motion.button
            whileTap={{ scale: 0.92 }}
            disabled={currentPage <= 1}
            onClick={() => onPageChange(1)}
            title="First Page"
            className="w-8 h-8 rounded-lg text-[12px] font-bold border border-zinc-200 bg-white text-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-100 flex items-center justify-center cursor-pointer transition-all shadow-2xs"
          >
            <FiChevronsLeft size={14} />
          </motion.button>

          {/* Previous Page Button */}
          <motion.button
            whileTap={{ scale: 0.92 }}
            disabled={currentPage <= 1}
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            title="Previous Page"
            className="px-2.5 h-8 rounded-lg text-[12px] font-bold border border-zinc-200 bg-white text-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-100 flex items-center gap-1 cursor-pointer transition-all shadow-2xs"
          >
            <FiChevronLeft size={14} /> Prev
          </motion.button>

          {/* Smart Windowed Page Number Buttons */}
          <div className="flex items-center gap-1">
            {(() => {
              let pages = [];
              if (totalPages <= 7) {
                pages = Array.from({ length: totalPages }, (_, i) => i + 1);
              } else if (currentPage <= 4) {
                pages = [1, 2, 3, 4, 5, "...", totalPages];
              } else if (currentPage >= totalPages - 3) {
                pages = [1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
              } else {
                pages = [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
              }

              return pages.map((item, idx) => {
                if (item === "...") {
                  return (
                    <span key={`dots-${idx}`} className="px-1 text-zinc-400 font-black text-[12px] select-none">
                      •••
                    </span>
                  );
                }

                const isActive = currentPage === item;
                return (
                  <motion.button
                    key={item}
                    whileTap={{ scale: 0.92 }}
                    onClick={() => onPageChange(item)}
                    className={`w-8 h-8 rounded-lg text-[12px] font-black transition-all cursor-pointer ${
                      isActive
                        ? "text-white shadow-xs"
                        : "bg-white border border-zinc-200 text-zinc-600 hover:bg-zinc-100"
                    }`}
                    style={
                      isActive
                        ? {
                            background: `linear-gradient(135deg,${T.olive},${T.olive2})`,
                            borderColor: "transparent",
                            boxShadow: `0 4px 12px rgba(107,138,90,0.35)`,
                          }
                        : {}
                    }
                  >
                    {item}
                  </motion.button>
                );
              });
            })()}
          </div>

          {/* Next Page Button */}
          <motion.button
            whileTap={{ scale: 0.92 }}
            disabled={currentPage >= totalPages}
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            title="Next Page"
            className="px-2.5 h-8 rounded-lg text-[12px] font-bold border border-zinc-200 bg-white text-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-100 flex items-center gap-1 cursor-pointer transition-all shadow-2xs"
          >
            Next <FiChevronRight size={14} />
          </motion.button>

          {/* Jump to Last Page Button */}
          <motion.button
            whileTap={{ scale: 0.92 }}
            disabled={currentPage >= totalPages}
            onClick={() => onPageChange(totalPages)}
            title="Last Page"
            className="w-8 h-8 rounded-lg text-[12px] font-bold border border-zinc-200 bg-white text-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-100 flex items-center justify-center cursor-pointer transition-all shadow-2xs"
          >
            <FiChevronsRight size={14} />
          </motion.button>
        </div>
      )}
    </div>
  );
};

// ─── MEAL CARD ────────────────────────────────────────────────────────────────
const MealCard = ({ meal, slot, onEdit, onSwap }) => (
  <motion.div whileHover={{ y:-3, boxShadow:"0 12px 32px rgba(0,0,0,0.08)" }}
    className="rounded-[18px] p-4 border relative overflow-hidden group"
    style={{ background:"rgba(255,255,255,0.9)", borderColor:T.border,
      boxShadow:"0 4px 16px rgba(0,0,0,0.04)", transition:"all 0.25s" }}>
    {/* Meal type label */}
    <div className="flex items-center justify-between mb-3">
      <span className="text-[10px] font-black uppercase tracking-[0.8px]"
        style={{ color: slot==="Breakfast"?T.orange:slot==="Lunch"?T.olive:slot==="Dinner"?T.blue:T.amber }}>
        {slot}
      </span>
      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <motion.button whileTap={{ scale:0.9 }} onClick={() => onSwap(slot)}
          className="w-6 h-6 rounded-[7px] flex items-center justify-center border-none cursor-pointer"
          style={{ background:"rgba(107,138,90,0.1)", color:T.olive }}>
          <FiCopy size={11}/>
        </motion.button>
        <motion.button whileTap={{ scale:0.9 }} onClick={() => onEdit(slot)}
          className="w-6 h-6 rounded-[7px] flex items-center justify-center border-none cursor-pointer"
          style={{ background:"rgba(242,154,12,0.1)", color:T.orange }}>
          <FiEdit3 size={11}/>
        </motion.button>
      </div>
    </div>

    {/* Meal name */}
    <p className="text-[13px] font-bold text-[#1F1F1F] leading-tight mb-3">{meal.name}</p>

    {/* Tags */}
    <div className="flex flex-wrap gap-1 mb-3">
      {meal.tags.slice(0,2).map(tag => (
        <span key={tag} className="text-[9px] font-bold px-2 py-0.5 rounded-full"
          style={{ background:"rgba(107,138,90,0.08)", color:T.olive, border:`1px solid rgba(107,138,90,0.18)` }}>
          {tag}
        </span>
      ))}
    </div>

    {/* Macros */}
    <div className="flex items-center justify-between pt-3 border-t"
      style={{ borderColor:"rgba(239,229,216,0.6)" }}>
      <div className="flex items-center gap-1">
        <span className="font-black text-[15px]" style={{ fontFamily:"'Poppins',sans-serif", color:T.orange }}>
          {meal.cal}
        </span>
        <span className="text-[10px] font-bold" style={{ color:T.muted }}>kcal</span>
      </div>
      <div className="flex gap-3">
        <MacroBadge label="Pro"  value={meal.protein} color={T.olive}/>
        <MacroBadge label="Carb" value={meal.carbs}   color={T.orange}/>
        <MacroBadge label="Fat"  value={meal.fat}     color={T.blue}/>
      </div>
    </div>
  </motion.div>
);

// ─── PLAN CARD (GRID) ─────────────────────────────────────────────────────────
const PlanCard = ({ plan, onOpen, onEdit, onDuplicate, onDelete, delay=0 }) => {
  const ts = TIER_S[plan.tier] || TIER_S.Free;
  return (
    <motion.div initial={{ opacity:0,y:14 }} animate={{ opacity:1,y:0 }} transition={{ delay }}
      whileHover={{ y:-5, boxShadow:"0 22px 55px rgba(0,0,0,0.10)", borderColor:"rgba(107,138,90,0.3)" }}
      className="rounded-[24px] overflow-hidden cursor-pointer relative group"
      style={{ background:"rgba(255,255,255,0.82)", border:`1px solid rgba(255,255,255,0.95)`,
        boxShadow:"0 8px 28px rgba(0,0,0,0.05)", transition:"all 0.28s" }}
      onClick={() => onOpen(plan.id)}>

      {/* Colour header strip */}
      <div className="h-2 w-full" style={{ background:ts.grad }}/>

      <div className="p-5">
        {/* Top row */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <TierPill tier={plan.tier}/>
              <StatusPill status={plan.status}/>
            </div>
            <p className="font-black text-[15px] text-[#1F1F1F]"
              style={{ fontFamily:"'Poppins',sans-serif", letterSpacing:"-0.4px" }}>
              {plan.name}
            </p>
            <p className="text-[11px] mt-1" style={{ color:T.muted }}>
              {plan.week} · {plan.region}
            </p>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          {[
            [plan.totalCal+"kcal","Daily avg",T.orange],
            [plan.subscribers,"Subscribers",T.olive],
            [plan.dietType,"Diet type","#534AB7"],
          ].map(([v,l,c]) => (
            <div key={l} className="rounded-[12px] p-2.5 text-center"
              style={{ background:"rgba(255,248,238,0.6)" }}>
              <p className="font-black text-[13px] leading-none"
                style={{ fontFamily:"'Poppins',sans-serif", color:c }}>{v}</p>
              <p className="text-[9px] font-bold uppercase tracking-[0.5px] mt-1"
                style={{ color:T.muted }}>{l}</p>
            </div>
          ))}
        </div>

        {/* Day coverage dots */}
        <div className="flex items-center gap-1.5 mb-4">
          {DAYS.map((d,i) => (
            <div key={d} title={d} className="flex flex-col items-center gap-1">
              <div className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold"
                style={{ background:plan.status==="Published"?`linear-gradient(135deg,${T.olive},${T.olive2})`:"rgba(239,229,216,0.8)",
                  color:plan.status==="Published"?"#fff":T.light }}>
                {d[0]}
              </div>
            </div>
          ))}
          <span className="text-[10px] font-semibold ml-1" style={{ color:T.muted }}>7/7 days</span>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t"
          style={{ borderColor:"rgba(239,229,216,0.5)" }}>
          <div className="flex items-center gap-1.5">
            {plan.autoPublish && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1"
                style={{ background:"rgba(34,197,94,0.1)", color:T.greenDk }}>
                <FiZap size={10}/> Auto-publish
              </span>
            )}
            <span className="text-[10px]" style={{ color:T.muted }}>{plan.version}</span>
          </div>
          <div className="flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
            {[
              { icon:FiEdit3,  fn:(e)=>{e.stopPropagation();onEdit(plan.id)},      tip:"Edit" },
              { icon:FiCopy,   fn:(e)=>{e.stopPropagation();onDuplicate(plan.id)}, tip:"Duplicate" },
              { icon:FiTrash2, fn:(e)=>{e.stopPropagation();onDelete(plan.id)},    tip:"Delete", danger:true },
            ].map(({ icon:Icon, fn, tip, danger }) => (
              <motion.button key={tip} whileTap={{ scale:0.9 }} onClick={fn} title={tip}
                className="w-7 h-7 rounded-[9px] flex items-center justify-center border cursor-pointer"
                style={{ background:"rgba(255,255,255,0.9)",
                  borderColor:danger?"rgba(226,75,74,0.2)":T.border,
                  color:danger?T.red:"#888", transition:"all 0.2s" }}>
                <Icon size={13}/>
              </motion.button>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ─── PLAN EDITOR DRAWER ───────────────────────────────────────────────────────
const PlanDrawer = ({ plan, onClose, showToast, onDelete, onEdit, onDuplicate, onToggleStatus }) => {
  const [activeDay, setActiveDay] = useState(0);
  if (!plan) return null;
  const ts = TIER_S[plan.tier] || TIER_S.Free;

  return (
    <>
      <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
        className="fixed inset-0 z-[100]"
        style={{ background:"rgba(15,15,15,0.2)", backdropFilter:"blur(4px)" }}
        onClick={onClose}/>
      <motion.div initial={{ x:28,opacity:0 }} animate={{ x:0,opacity:1 }}
        exit={{ x:28,opacity:0 }} transition={{ duration:0.35,ease:[0.25,1,0.5,1] }}
        className="ad-drawer fixed right-0 top-0 bottom-0 z-[101] overflow-y-auto"
        style={{ width:560, background:"rgba(255,253,248,0.97)",
          borderLeft:`1px solid rgba(239,229,216,0.7)`,
          boxShadow:"-24px 0 80px rgba(0,0,0,0.1)", scrollbarWidth:"none" }}>

        {/* Sticky header */}
        <div className="sticky top-0 z-10 border-b"
          style={{ background:"rgba(255,253,248,0.95)", backdropFilter:"blur(12px)",
            borderColor:"rgba(239,229,216,0.8)" }}>
          <div className="px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-[12px] flex items-center justify-center text-white text-[12px] font-black"
                style={{ background:ts.grad }}>
                {plan.tier[0]}
              </div>
              <div>
                <p className="font-black text-[15px] text-[#1F1F1F]"
                  style={{ fontFamily:"'Poppins',sans-serif" }}>
                  {plan.name}
                </p>
                <p className="text-[11px] text-[#888]">
                  {plan.tier} Tier · {plan.dietType} · {plan.region}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <StatusPill status={plan.status}/>
              <motion.button whileHover={{ background:"rgba(239,229,216,0.9)" }} onClick={onClose}
                className="w-8 h-8 rounded-[10px] flex items-center justify-center border-none cursor-pointer"
                style={{ background:"rgba(239,229,216,0.5)", color:"#888" }}>
                <FiX size={15}/>
              </motion.button>
            </div>
          </div>

          {/* Day selection tabs */}
          <div className="px-6 pb-3 flex gap-1.5 overflow-x-auto" style={{ scrollbarWidth:"none" }}>
            {DAYS.map((day, i) => (
              <motion.button key={day} whileTap={{ scale:0.96 }} onClick={() => setActiveDay(i)}
                className="px-3.5 py-1.5 rounded-full text-[11px] font-bold border cursor-pointer whitespace-nowrap"
                style={{
                  background: activeDay === i ? `linear-gradient(135deg,${T.olive},${T.olive2})` : "rgba(255,255,255,0.8)",
                  color: activeDay === i ? "#fff" : T.mid,
                  borderColor: activeDay === i ? "transparent" : T.border,
                  boxShadow: activeDay === i ? `0 4px 12px rgba(107,138,90,0.3)` : "none"
                }}>
                {day}
              </motion.button>
            ))}
          </div>
        </div>

        {/* Drawer content */}
        <div className="px-6 py-4 border-b" style={{ borderColor:"rgba(239,229,216,0.5)" }}>
          <div className="flex items-center justify-between mb-3">
            <p className="font-bold text-[13px] text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif" }}>
              {DAYS[activeDay]} Meals Overview
            </p>
            <div className="flex items-center gap-2">
              <motion.button whileHover={{ scale:1.02 }} whileTap={{ scale:0.97 }}
                onClick={() => { onEdit?.(plan); onClose(); }}
                className="h-8 px-3 rounded-full text-white text-[11px] font-bold flex items-center gap-1.5 border-none cursor-pointer"
                style={{ background:`linear-gradient(135deg,${T.olive},${T.olive2})`,
                  boxShadow:`0 4px 12px rgba(107,138,90,0.35)`, fontFamily:"'Inter',sans-serif" }}>
                <FiEdit3 size={11}/> Full 7-Day Editor
              </motion.button>
            </div>
          </div>
          {/* Day calorie total */}
          {(() => {
            const dayMeals = plan.days?.[activeDay]?.meals || [];
            const totalCal = dayMeals.reduce((a,m) => a + (m.cal || m.calories || 0), 0);
            const totalP   = dayMeals.reduce((a,m) => a + (m.protein || 0), 0);
            const totalC   = dayMeals.reduce((a,m) => a + (m.carbs || 0), 0);
            const totalF   = dayMeals.reduce((a,m) => a + (m.fat || 0), 0);
            return (
              <div className="grid grid-cols-4 gap-2 mt-3">
                {[[totalCal+"kcal","Calories",T.orange],[totalP+"g","Protein",T.olive],[totalC+"g","Carbs","#534AB7"],[totalF+"g","Fat",T.amber]].map(([v,l,c]) => (
                  <div key={l} className="rounded-[12px] p-2.5 text-center"
                    style={{ background:"rgba(255,248,238,0.6)" }}>
                    <p className="font-black text-[14px] leading-none"
                      style={{ fontFamily:"'Poppins',sans-serif", color:c }}>{v}</p>
                    <p className="text-[9px] font-bold uppercase tracking-[0.5px] mt-1"
                      style={{ color:T.muted }}>{l}</p>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>

        {/* Meal cards */}
        <div className="px-6 py-5 flex flex-col gap-4">
          <AnimatePresence mode="wait">
            <motion.div key={activeDay} initial={{ opacity:0,x:16 }} animate={{ opacity:1,x:0 }}
              exit={{ opacity:0,x:-16 }} transition={{ duration:0.3,ease:[0.25,1,0.5,1] }}
              className="flex flex-col gap-4">
              {(plan.days?.[activeDay]?.meals || []).map((meal, i) => (
                <MealCard key={i} meal={meal} slot={meal.slot || meal.mealSlot || MEALS[i]}
                  onEdit={() => { onEdit?.(plan); onClose(); }}
                  onSwap={() => { onEdit?.(plan); onClose(); }}/>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Admin actions */}
        <div className="px-6 pb-6 flex flex-col gap-2.5">
          <div className="border-t pt-5 mb-1" style={{ borderColor:"rgba(239,229,216,0.5)" }}>
            <p className="text-[9px] font-black uppercase tracking-[1.2px] mb-3" style={{ color:T.light }}>Plan Actions</p>
          </div>
          {plan.status !== "Published" && (
            <motion.button whileHover={{ y:-1 }} onClick={() => { onToggleStatus?.(plan); onClose(); }}
              className="w-full h-10 rounded-full text-white font-bold text-[13px] flex items-center justify-center gap-2 border-none cursor-pointer"
              style={{ background:`linear-gradient(135deg,${T.olive},${T.olive2})`,
                boxShadow:`0 6px 18px rgba(107,138,90,0.38)`, fontFamily:"'Inter',sans-serif" }}>
              <FiCheckCircle size={14}/> Publish Plan Live to Users
            </motion.button>
          )}
          {plan.status === "Published" && (
            <motion.button whileHover={{ y:-1 }} onClick={() => { onToggleStatus?.(plan); onClose(); }}
              className="w-full h-10 rounded-full font-bold text-[13px] flex items-center justify-center gap-2 border-none cursor-pointer"
              style={{ background:"rgba(245,158,11,0.1)", color:T.amber,
                border:"1px solid rgba(245,158,11,0.25)", fontFamily:"'Inter',sans-serif" }}>
              Unpublish Plan (Set to Draft)
            </motion.button>
          )}
          <div className="grid grid-cols-2 gap-2.5">
            <motion.button whileHover={{ y:-1 }} onClick={() => { onDuplicate?.(plan); onClose(); }}
              className="h-10 rounded-full font-semibold text-[12px] flex items-center justify-center gap-1.5 cursor-pointer"
              style={{ background:"rgba(255,255,255,0.9)", border:`1.5px solid ${T.border}`,
                color:T.mid, fontFamily:"'Inter',sans-serif" }}>
              <FiCopy size={13}/> Duplicate Plan
            </motion.button>
            <motion.button whileHover={{ y:-1 }} onClick={() => { exportPlanAsFile(plan); showToast("✓ Plan exported as TXT"); }}
              className="h-10 rounded-full font-semibold text-[12px] flex items-center justify-center gap-1.5 cursor-pointer"
              style={{ background:"rgba(255,255,255,0.9)", border:`1.5px solid ${T.border}`,
                color:T.mid, fontFamily:"'Inter',sans-serif" }}>
              <FiDownload size={13}/> Export Plan
            </motion.button>
          </div>
          <motion.button whileHover={{ background:"rgba(226,75,74,0.14)" }}
            onClick={() => { onDelete?.(plan.id); onClose(); }}
            className="w-full h-10 rounded-full font-bold text-[13px] flex items-center justify-center gap-2 cursor-pointer border"
            style={{ background:"rgba(226,75,74,0.08)", color:T.red,
              borderColor:"rgba(226,75,74,0.2)", fontFamily:"'Inter',sans-serif", transition:"background 0.2s" }}>
            <FiTrash2 size={13}/> Delete Plan
          </motion.button>
        </div>
      </motion.div>
    </>
  );
};

// ─── CREATE / EDIT MODAL (DYNAMIC 7-DAY MEALS & SCHEDULE EDITOR) ─────────────
const PlanModal = ({ plan, onClose, onSaveSuccess, showToast }) => {
  const { tiers: dynamicTiers } = useDynamicPackages();
  const [activeTab, setActiveTab] = useState("details"); // "details" | "meals"
  const [activeDayIdx, setActiveDayIdx] = useState(0);

  const [name, setName] = useState(plan?.name || "");
  const [tier, setTier] = useState(plan?.tier || "");
  
  const standardDiets = ["Veg","Non-Veg","Vegan","Eggetarian","Jain","Pescatarian","Keto","Sattvic"];
  const standardRegions = ["Indian (North)","Indian (South)","American / Western","Mediterranean","Asian","Middle Eastern","Global / Fusion"];
  const standardGoals = ["Low Calorie (Weight Loss)","Balanced Wellness","High Calorie (Weight Gain)","High Protein (Muscle)","Low Carb / Keto","Detox / Immunity"];

  const [dietType, setDietType] = useState(() => {
    if (!plan?.dietType) return "";
    return standardDiets.includes(plan.dietType) ? plan.dietType : "Custom / Other";
  });
  const [customDietType, setCustomDietType] = useState(() => {
    if (!plan?.dietType) return "";
    return standardDiets.includes(plan.dietType) ? "" : plan.dietType;
  });

  const [region, setRegion] = useState(() => {
    if (!plan?.region) return "";
    return standardRegions.includes(plan.region) ? plan.region : "Custom / Other";
  });
  const [customRegion, setCustomRegion] = useState(() => {
    if (!plan?.region) return "";
    return standardRegions.includes(plan.region) ? "" : plan.region;
  });

  const [calorieGoal, setCalorieGoal] = useState(() => {
    if (!plan?.calorieGoal) return "";
    return standardGoals.includes(plan.calorieGoal) ? plan.calorieGoal : "Custom / Other";
  });
  const [customCalorieGoal, setCustomCalorieGoal] = useState(() => {
    if (!plan?.calorieGoal) return "";
    return standardGoals.includes(plan.calorieGoal) ? "" : plan.calorieGoal;
  });

  const [totalCal, setTotalCal] = useState(plan?.totalCal || plan?.totalCalories ? String(plan.totalCal || plan.totalCalories) : "");
  const [description, setDescription] = useState(plan?.description || "");
  const [status, setStatus] = useState(plan?.status || "Published");
  const [submitting, setSubmitting] = useState(false);
  const isEdit = !!plan;

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const validateName = (val) => {
    const v = (val || "").trim();
    if (!v) return "Plan title is required.";
    if (v.length < 3) return "Plan title must be at least 3 characters.";
    if (v.length > 100) return "Plan title cannot exceed 100 characters.";
    if (/<script|javascript:/i.test(v)) return "Plan title contains forbidden script tags.";
    if (!/^[a-zA-Z0-9\s]+$/.test(v)) return "Plan title cannot contain special characters. Only alphanumeric characters and spaces are allowed.";
    return "";
  };

  const validateTier = (val) => {
    const v = (val || "").trim();
    if (!v) return "Target user tier is required. Please select a tier.";
    return "";
  };

  const validateDietType = (dt, customDt) => {
    const v = (dt || "").trim();
    if (!v) return "Dietary category is required. Please select a diet category.";
    if (v === "Custom / Other") {
      const c = (customDt || "").trim();
      if (!c) return "Please enter your custom dietary category.";
      if (c.length < 2) return "Custom diet category must be at least 2 characters.";
      if (c.length > 60) return "Custom diet category cannot exceed 60 characters.";
      if (!/^[a-zA-Z0-9\s\-&]+$/.test(c)) return "Custom diet category can only contain letters, numbers, spaces, and hyphens.";
    }
    return "";
  };

  const validateRegion = (rg, customRg) => {
    const v = (rg || "").trim();
    if (!v) return "Region / cuisine is required. Please select a region.";
    if (v === "Custom / Other") {
      const c = (customRg || "").trim();
      if (!c) return "Please enter your custom cuisine / region.";
      if (c.length < 2) return "Custom cuisine must be at least 2 characters.";
      if (c.length > 60) return "Custom cuisine cannot exceed 60 characters.";
    }
    return "";
  };

  const validateCalorieGoal = (cg, customCg) => {
    const v = (cg || "").trim();
    if (!v) return "Caloric / health goal is required. Please select a goal.";
    if (v === "Custom / Other") {
      const c = (customCg || "").trim();
      if (!c) return "Please enter your custom calorie / health goal.";
      if (c.length < 2) return "Custom goal must be at least 2 characters.";
      if (c.length > 60) return "Custom goal cannot exceed 60 characters.";
    }
    return "";
  };

  const validateTotalCal = (val) => {
    const v = String(val || "").trim();
    if (!v) return "Target daily calories is required.";
    const num = Number(v);
    if (isNaN(num) || num < 500 || num > 10000) return "Target calories must be a number between 500 and 10,000 kcal.";
    return "";
  };

  const validateDescription = (val) => {
    if (!val) return "";
    if (val.length > 3000) return "Description cannot exceed 3000 characters.";
    if (/<script|javascript:/i.test(val)) return "Description contains forbidden script tags.";
    return "";
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    let err = "";
    if (field === "name") err = validateName(name);
    if (field === "tier") err = validateTier(tier);
    if (field === "dietType") err = validateDietType(dietType, customDietType);
    if (field === "region") err = validateRegion(region, customRegion);
    if (field === "calorieGoal") err = validateCalorieGoal(calorieGoal, customCalorieGoal);
    if (field === "totalCal") err = validateTotalCal(totalCal);
    if (field === "description") err = validateDescription(description);
    setErrors(prev => ({ ...prev, [field]: err }));
  };

  // Initialize 7-Day Meals Schedule State (Clean / No Hardcoded meals for new plans)
  const [schedule, setSchedule] = useState(() => {
    const init = {};
    const defaultTimes = {
      "Breakfast": "08:00 AM",
      "Lunch": "01:30 PM",
      "Snack": "05:00 PM",
      "Dinner": "08:30 PM"
    };

    DAYS.forEach(day => {
      init[day] = [];
      const dayRawMeals = plan?.rawMeals?.filter(m => m.day === day) || [];
      if (dayRawMeals.length > 0) {
        dayRawMeals.forEach(found => {
          init[day].push({
            id: found.id || Math.random().toString(),
            mealSlot: found.mealSlot,
            time: found.time || defaultTimes[found.mealSlot] || "08:00 AM",
            mealName: found.mealName,
            description: found.description || found.Description || found.notes || found.Notes || "",
            calories: found.calories,
            protein: found.protein,
            carbs: found.carbs,
            fat: found.fat,
            prepTime: found.prepTime || "15 min",
            notes: found.description || found.Description || found.notes || found.Notes || ""
          });
        });
      }
    });
    return init;
  });

  const updateMealField = (day, mealId, field, val) => {
    setSchedule(prev => ({
      ...prev,
      [day]: prev[day].map(m => m.id === mealId ? { ...m, [field]: val } : m)
    }));
  };

  const addCustomMealRow = (day) => {
    const newRow = {
      id: Math.random().toString(),
      mealSlot: "Custom Snack / Warm Drink",
      time: "07:00 AM",
      mealName: "Green Tea / Warm Lemon Honey Water",
      calories: 50,
      protein: 1,
      carbs: 8,
      fat: 0,
      prepTime: "5 min",
      notes: "Metabolism booster"
    };
    setSchedule(prev => ({
      ...prev,
      [day]: [...prev[day], newRow]
    }));
  };

  const removeMealRow = (day, mealId) => {
    setSchedule(prev => ({
      ...prev,
      [day]: prev[day].filter(m => m.id !== mealId)
    }));
  };

  const copyDayToAll = (sourceDay) => {
    const sourceRows = schedule[sourceDay];
    setSchedule(prev => {
      const next = { ...prev };
      DAYS.forEach(d => {
        next[d] = sourceRows.map(r => ({ ...r, id: Math.random().toString() }));
      });
      return next;
    });
    showToast(`✓ Copied ${sourceDay}'s ${sourceRows.length} meals to all 7 days!`);
  };

  const inputSty = {
    width:"100%", border:`1.5px solid ${T.border}`, background:"rgba(255,255,255,0.9)",
    borderRadius:12, padding:"8px 12px", fontSize:12, fontFamily:"'Inter',sans-serif",
    color:T.dark, outline:"none", transition:"border-color 0.2s, box-shadow 0.2s",
  };

  const validateAllDetails = () => {
    const nErr = validateName(name);
    const tErr = validateTier(tier);
    const dtErr = validateDietType(dietType, customDietType);
    const rgErr = validateRegion(region, customRegion);
    const cgErr = validateCalorieGoal(calorieGoal, customCalorieGoal);
    const calErr = validateTotalCal(totalCal);
    const descErr = validateDescription(description);

    const newErrs = {
      name: nErr,
      tier: tErr,
      dietType: dtErr,
      region: rgErr,
      calorieGoal: cgErr,
      totalCal: calErr,
      description: descErr
    };

    setErrors(newErrs);
    setTouched({
      name: true,
      tier: true,
      dietType: true,
      region: true,
      calorieGoal: true,
      totalCal: true,
      description: true
    });

    if (nErr || tErr || dtErr || rgErr || cgErr || calErr || descErr) {
      showToast("Please fix all validation errors before proceeding.");
      return false;
    }
    return true;
  };

  const normalizeMealTime = (t) => {
    if (!t) return "";
    const clean = String(t).trim().toLowerCase().replace(/\s+/g, "");
    const m12 = clean.match(/^(\d{1,2})(?::(\d{2}))?(am|pm)$/);
    if (m12) {
      const hh = m12[1].padStart(2, "0");
      const mm = m12[2] || "00";
      const ampm = m12[3];
      return `${hh}:${mm}${ampm}`;
    }
    const m24 = clean.match(/^(\d{1,2}):(\d{2})$/);
    if (m24) {
      const hh = m24[1].padStart(2, "0");
      const mm = m24[2];
      return `${hh}:${mm}`;
    }
    return clean;
  };

  const validateMealsSchedule = () => {
    let hasAtLeastOneMeal = false;
    for (let i = 0; i < DAYS.length; i++) {
      const day = DAYS[i];
      const dayMeals = schedule[day] || [];
      if (dayMeals.length > 0) hasAtLeastOneMeal = true;

      // Duplicate time tracking per day
      const seenTimesOnDay = new Map();

      for (let j = 0; j < dayMeals.length; j++) {
        const m = dayMeals[j];
        const slotTitle = (m.mealSlot || "").trim();
        const mealTitle = (m.mealName || "").trim();
        const rawTime = (m.time || "").trim();

        if (!rawTime) {
          setActiveTab("meals");
          setActiveDayIdx(i);
          showToast(`Please enter a meal timing (e.g. 08:00 AM) for ${slotTitle || `meal #${j + 1}`} on ${day}.`);
          return false;
        }

        const normTime = normalizeMealTime(rawTime);
        if (normTime) {
          if (seenTimesOnDay.has(normTime)) {
            const firstSlot = seenTimesOnDay.get(normTime);
            setActiveTab("meals");
            setActiveDayIdx(i);
            showToast(`❌ Timing Conflict on ${day}: "${firstSlot}" and "${slotTitle || 'Meal'}" are both scheduled at "${rawTime}". Each meal on ${day} must have a distinct time.`);
            return false;
          }
          seenTimesOnDay.set(normTime, slotTitle || `Meal #${j + 1}`);
        }

        if (!slotTitle) {
          setActiveTab("meals");
          setActiveDayIdx(i);
          showToast(`Please enter a meal slot name (e.g. Breakfast, Lunch) for meal #${j + 1} on ${day}.`);
          return false;
        }

        if (slotTitle.length < 2 || slotTitle.length > 50) {
          setActiveTab("meals");
          setActiveDayIdx(i);
          showToast(`Meal slot name "${slotTitle}" on ${day} must be between 2 and 50 characters.`);
          return false;
        }

        if (!/^[a-zA-Z0-9\s.'\-()&/]+$/.test(slotTitle)) {
          setActiveTab("meals");
          setActiveDayIdx(i);
          showToast(`Meal slot name "${slotTitle}" on ${day} contains invalid special characters.`);
          return false;
        }

        if (!mealTitle) {
          setActiveTab("meals");
          setActiveDayIdx(i);
          showToast(`Please enter a meal name for ${slotTitle} on ${day}.`);
          return false;
        }

        if (mealTitle.length < 2 || mealTitle.length > 100) {
          setActiveTab("meals");
          setActiveDayIdx(i);
          showToast(`Meal name "${mealTitle}" on ${day} must be between 2 and 100 characters.`);
          return false;
        }

        if (!/^[a-zA-Z0-9\s.'\-(),&/]+$/.test(mealTitle)) {
          setActiveTab("meals");
          setActiveDayIdx(i);
          showToast(`Meal name "${mealTitle}" on ${day} contains invalid special characters.`);
          return false;
        }

        if (/<script|javascript:/i.test(mealTitle) || /<script|javascript:/i.test(m.description || "") || /<script|javascript:/i.test(slotTitle)) {
          setActiveTab("meals");
          setActiveDayIdx(i);
          showToast(`Forbidden script tags detected in meal #${j + 1} on ${day}.`);
          return false;
        }

        const calNum = Number(m.calories);
        if (isNaN(calNum) || calNum < 0 || calNum > 5000) {
          setActiveTab("meals");
          setActiveDayIdx(i);
          showToast(`Calories for "${mealTitle}" on ${day} must be between 0 and 5000 kcal.`);
          return false;
        }

        const protNum = Number(m.protein);
        const carbNum = Number(m.carbs);
        const fatNum = Number(m.fat);
        if (isNaN(protNum) || protNum < 0 || isNaN(carbNum) || carbNum < 0 || isNaN(fatNum) || fatNum < 0 || protNum > 500 || carbNum > 500 || fatNum > 500) {
          setActiveTab("meals");
          setActiveDayIdx(i);
          showToast(`Macros (Protein, Carbs, Fat) for "${mealTitle}" on ${day} must be valid numbers between 0 and 500g.`);
          return false;
        }
      }
    }

    if (!hasAtLeastOneMeal) {
      setActiveTab("meals");
      showToast("Please add at least one meal to your 7-day schedule or generate meals with AI.");
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateAllDetails()) {
      return;
    }

    if (!validateMealsSchedule()) {
      return;
    }

    const finalDietType = dietType === "Custom / Other" ? customDietType.trim() : dietType;
    const finalRegion = region === "Custom / Other" ? customRegion.trim() : region;
    const finalCalorieGoal = calorieGoal === "Custom / Other" ? customCalorieGoal.trim() : calorieGoal;

    setSubmitting(true);
    try {
      const compiledMeals = [];
      DAYS.forEach(day => {
        schedule[day].forEach(m => {
          compiledMeals.push({
            day,
            mealSlot: m.mealSlot || "Meal",
            mealName: m.mealName || "Healthy Option",
            description: m.description || m.notes || "Nutritionist recommended recipe.",
            time: m.time || "08:00 AM",
            calories: Number(m.calories) || 200,
            protein: Number(m.protein) || 10,
            carbs: Number(m.carbs) || 25,
            fat: Number(m.fat) || 5,
            prepTime: m.prepTime || "15 min",
            notes: m.description || m.notes || "Nutritionist recommended.",
            tags: "Healthy"
          });
        });
      });

      const payload = {
        id: plan?.id,
        name: name.trim(),
        tier,
        dietType: finalDietType,
        region: finalRegion,
        calorieGoal: finalCalorieGoal,
        totalCalories: Number(totalCal),
        description: description.trim(),
        status,
        meals: compiledMeals
      };

      const res = await dietPlanAPI.saveAdminPlan(payload);
      if (res.data?.success) {
        showToast(isEdit ? "✓ Plan updated & saved!" : "✓ 7-Day Plan created & push notifications sent!");
        onSaveSuccess();
        onClose();
      }
    } catch (e) {
      console.error("Save plan error", e);
      const serverMsg = e.response?.data?.message || e.response?.data?.Message || e.response?.data?.error || e.message || "Error saving plan. Please check all fields.";
      showToast(`❌ ${serverMsg}`);
    } finally {
      setSubmitting(false);
    }
  };

  const [aiLoading, setAiLoading] = useState(false);

  const handleAiAutofill = async () => {
    if (!tier) {
      setTouched(prev => ({ ...prev, tier: true }));
      setErrors(prev => ({ ...prev, tier: "Please select a Target Tier first." }));
      showToast("Please select a Target Tier first.");
      return;
    }
    if (!dietType) {
      setTouched(prev => ({ ...prev, dietType: true }));
      setErrors(prev => ({ ...prev, dietType: "Please select a Dietary Category first." }));
      showToast("Please select a Dietary Category first.");
      return;
    }
    if (!region) {
      setTouched(prev => ({ ...prev, region: true }));
      setErrors(prev => ({ ...prev, region: "Please select a Cuisine / Region first." }));
      showToast("Please select a Cuisine / Region first.");
      return;
    }
    if (!calorieGoal) {
      setTouched(prev => ({ ...prev, calorieGoal: true }));
      setErrors(prev => ({ ...prev, calorieGoal: "Please select a Calorie / Health Goal first." }));
      showToast("Please select a Calorie / Health Goal first.");
      return;
    }
    if (!totalCal) {
      setTouched(prev => ({ ...prev, totalCal: true }));
      setErrors(prev => ({ ...prev, totalCal: "Please enter Target Daily Calories first." }));
      showToast("Please enter Target Daily Calories first.");
      return;
    }

    setAiLoading(true);
    try {
      const finalDietType = dietType === "Custom / Other" ? customDietType.trim() : dietType;
      const finalRegion = region === "Custom / Other" ? customRegion.trim() : region;
      const finalCalorieGoal = calorieGoal === "Custom / Other" ? customCalorieGoal.trim() : calorieGoal;

      const res = await dietPlanAPI.generateAiPlan({
        tier,
        dietType: finalDietType || "Veg",
        region: finalRegion || "Indian (North)",
        calorieGoal: finalCalorieGoal || "Balanced Wellness",
        totalCalories: Number(totalCal) || 1800,
        specialInstructions: description || undefined
      });

      if (res.data?.success && res.data?.data) {
        const gen = res.data.data;
        if (!name.trim() && gen.name) {
          const cleanName = gen.name.replace(/[^a-zA-Z0-9\s]/g, "");
          setName(cleanName);
          if (touched.name) setErrors(prev => ({ ...prev, name: validateName(cleanName) }));
        }
        if (gen.description) setDescription(gen.description);
        if (gen.totalCalories && !totalCal) setTotalCal(String(gen.totalCalories));

        if (gen.meals && gen.meals.length > 0) {
          const newSchedule = {};
          DAYS.forEach(d => {
            const dayMeals = gen.meals.filter(m => m.day === d);
            newSchedule[d] = dayMeals.map(m => ({
              id: Math.random().toString(),
              mealSlot: m.mealSlot,
              time: m.time || (m.mealSlot === "Breakfast" ? "08:00 AM" : m.mealSlot === "Lunch" ? "01:30 PM" : m.mealSlot === "Snack" ? "05:00 PM" : "07:30 PM"),
              mealName: m.mealName,
              description: m.description || m.notes || "",
              calories: m.calories || 200,
              protein: m.protein || 10,
              carbs: m.carbs || 30,
              fat: m.fat || 5,
              prepTime: m.prepTime || "15 min",
              notes: m.notes || m.description || ""
            }));
          });
          setSchedule(newSchedule);
          showToast(`✓ AI generated plan overview & 28-meal menu! Review description below and click Next.`);
        }
      }
    } catch (e) {
      console.error("AI Auto-fill error", e);
      showToast(e.response?.data?.message || "Failed to generate AI meals. Please check server connection.");
    } finally {
      setAiLoading(false);
    }
  };

  const totalMealsCount = DAYS.reduce((acc, d) => acc + (schedule[d]?.length || 0), 0);
  const hasMeals = totalMealsCount > 0;

  const currentDayName = DAYS[activeDayIdx];

  return (
    <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ background:"rgba(15,15,15,0.38)", backdropFilter:"blur(6px)" }}
      onClick={onClose}>
      <motion.div initial={{ scale:0.96,opacity:0 }} animate={{ scale:1,opacity:1 }}
        exit={{ scale:0.96,opacity:0 }} transition={{ duration:0.3,ease:[0.25,1,0.5,1] }}
        onClick={e => e.stopPropagation()}
        className="ad-modal ad-pad relative rounded-[28px] p-6 overflow-y-auto"
        style={{ width:820, maxHeight:"92vh", background:"rgba(255,253,248,0.98)",
          boxShadow:"0 40px 100px rgba(0,0,0,0.22)", border:"1px solid rgba(255,255,255,0.9)",
          scrollbarWidth:"none" }}>

        <div className="relative">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-[13px] flex items-center justify-center"
                style={{ background:`linear-gradient(135deg,${T.olive},${T.olive2})`,
                  boxShadow:`0 5px 14px rgba(107,138,90,0.4)` }}>
                <FiCalendar size={18} style={{ color:"#fff" }}/>
              </div>
              <div>
                <p className="font-black text-[16px] text-[#1F1F1F]"
                  style={{ fontFamily:"'Poppins',sans-serif" }}>
                  {isEdit ? "Edit 7-Day Diet Plan" : "Create 7-Day Diet Plan"}
                </p>
                <p className="text-[11px] mt-0.5" style={{ color:T.muted }}>
                  Build full 7-day weekly nutrition schedule {tier ? `for ${tier} Tier users` : "for assigned users"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <motion.button whileHover={{ background:"rgba(239,229,216,0.9)" }} onClick={onClose}
                className="w-8 h-8 rounded-[10px] flex items-center justify-center border-none cursor-pointer"
                style={{ background:"rgba(239,229,216,0.5)", color:"#888" }}>
                <FiX size={15}/>
              </motion.button>
            </div>
          </div>

          {/* Mode Switcher Tabs */}
          <div className="flex gap-2 p-1 rounded-[16px] mb-5" style={{ background:"rgba(239,229,216,0.4)" }}>
            <button type="button" onClick={() => setActiveTab("details")}
              className="flex-1 py-2 rounded-[12px] text-[12px] font-bold border-none cursor-pointer flex items-center justify-center gap-1.5"
              style={activeTab === "details"
                ? { background:"#fff", color:T.dark, boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }
                : { background:"transparent", color:T.muted }}>
              <FiSettings size={14}/> 1. Plan Settings & Overview
            </button>
            <button type="button" onClick={() => {
                if (!validateAllDetails()) {
                  return;
                }
                if (!hasMeals && !isEdit && !description) {
                  showToast("Please generate the plan overview & meals first.");
                  return;
                }
                setActiveTab("meals");
              }}
              className="flex-1 py-2 rounded-[12px] text-[12px] font-bold border-none cursor-pointer flex items-center justify-center gap-1.5"
              style={activeTab === "meals"
                ? { background:"#fff", color:T.dark, boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }
                : { background:"transparent", color:T.muted }}>
              <FiCalendar size={14}/> 2. 7-Day Meals & Timing Builder ({hasMeals ? `${totalMealsCount} Meals Ready` : "0 Meals"})
            </button>
          </div>

          {activeTab === "details" && (
            <div className="flex flex-col gap-4">
              <div>
                <label className="text-[10px] font-black uppercase tracking-[0.9px] block mb-1.5"
                  style={{ color:T.muted }}>Plan Title <span className="text-red-500">*</span></label>
                <input
                  style={{
                    ...inputSty,
                    borderColor: touched.name && errors.name ? "#EF4444" : T.border,
                    background: touched.name && errors.name ? "rgba(254,242,242,0.6)" : "rgba(255,255,255,0.9)"
                  }}
                  value={name}
                  onChange={e => {
                    setName(e.target.value);
                    if (touched.name) setErrors(prev => ({ ...prev, name: validateName(e.target.value) }));
                  }}
                  onBlur={() => handleBlur("name")}
                  placeholder="e.g. Free Tier Weekly Wellness Menu"
                />
                {touched.name && errors.name && (
                  <p className="text-[11px] font-bold text-red-500 flex items-center gap-1 mt-1">
                    <FiAlertTriangle size={11} className="shrink-0" /> {errors.name}
                  </p>
                )}
              </div>

              <div className="ad-1col grid grid-cols-3 gap-3">
                <div>
                  <CustomSelect
                    label="Target User Tier *"
                    placeholder="Select Target Tier"
                    value={tier}
                    hasError={touched.tier && !!errors.tier}
                    onChange={val => {
                      setTier(val);
                      setTouched(prev => ({ ...prev, tier: true }));
                      setErrors(prev => ({ ...prev, tier: validateTier(val) }));
                    }}
                    options={dynamicTiers.map(t => ({ label: `${t} Tier Only`, value: t }))}
                  />
                  {touched.tier && errors.tier && (
                    <p className="text-[11px] font-bold text-red-500 flex items-center gap-1 mt-1">
                      <FiAlertTriangle size={11} className="shrink-0" /> {errors.tier}
                    </p>
                  )}
                </div>

                <div>
                  <CustomSelect
                    label="Dietary Category *"
                    placeholder="Select Dietary Category"
                    value={dietType}
                    hasError={touched.dietType && !!errors.dietType}
                    onChange={val => {
                      setDietType(val);
                      setTouched(prev => ({ ...prev, dietType: true }));
                      setErrors(prev => ({ ...prev, dietType: validateDietType(val, customDietType) }));
                    }}
                    options={["Veg","Non-Veg","Vegan","Eggetarian","Jain","Pescatarian","Keto","Sattvic", "Custom / Other"]}
                  />
                  {dietType === "Custom / Other" && (
                    <input
                      style={{
                        ...inputSty,
                        marginTop: 6,
                        borderColor: touched.dietType && errors.dietType ? "#EF4444" : T.border
                      }}
                      value={customDietType}
                      onChange={e => {
                        setCustomDietType(e.target.value);
                        if (touched.dietType) setErrors(prev => ({ ...prev, dietType: validateDietType("Custom / Other", e.target.value) }));
                      }}
                      onBlur={() => handleBlur("dietType")}
                      placeholder="Enter custom diet category"
                    />
                  )}
                  {touched.dietType && errors.dietType && (
                    <p className="text-[11px] font-bold text-red-500 flex items-center gap-1 mt-1">
                      <FiAlertTriangle size={11} className="shrink-0" /> {errors.dietType}
                    </p>
                  )}
                </div>

                <div>
                  <CustomSelect
                    label="Plan Status"
                    placeholder="Select Status"
                    value={status}
                    onChange={setStatus}
                    options={[
                      { label: "Published (Live)", value: "Published" },
                      { label: "Draft (Hidden)", value: "Draft" },
                      { label: "Archived", value: "Archived" }
                    ]}
                  />
                </div>
              </div>

              <div className="ad-1col grid grid-cols-2 gap-3">
                <div>
                  <CustomSelect
                    label="Region / Cuisine Dimension *"
                    placeholder="Select Cuisine / Region"
                    value={region}
                    hasError={touched.region && !!errors.region}
                    onChange={val => {
                      setRegion(val);
                      setTouched(prev => ({ ...prev, region: true }));
                      setErrors(prev => ({ ...prev, region: validateRegion(val, customRegion) }));
                    }}
                    options={["Indian (North)","Indian (South)","American / Western","Mediterranean","Asian","Middle Eastern","Global / Fusion", "Custom / Other"]}
                  />
                  {region === "Custom / Other" && (
                    <input
                      style={{
                        ...inputSty,
                        marginTop: 6,
                        borderColor: touched.region && errors.region ? "#EF4444" : T.border
                      }}
                      value={customRegion}
                      onChange={e => {
                        setCustomRegion(e.target.value);
                        if (touched.region) setErrors(prev => ({ ...prev, region: validateRegion("Custom / Other", e.target.value) }));
                      }}
                      onBlur={() => handleBlur("region")}
                      placeholder="Enter custom cuisine / region"
                    />
                  )}
                  {touched.region && errors.region && (
                    <p className="text-[11px] font-bold text-red-500 flex items-center gap-1 mt-1">
                      <FiAlertTriangle size={11} className="shrink-0" /> {errors.region}
                    </p>
                  )}
                </div>

                <div>
                  <CustomSelect
                    label="Caloric / Health Goal *"
                    placeholder="Select Calorie / Health Goal"
                    value={calorieGoal}
                    hasError={touched.calorieGoal && !!errors.calorieGoal}
                    onChange={val => {
                      setCalorieGoal(val);
                      setTouched(prev => ({ ...prev, calorieGoal: true }));
                      setErrors(prev => ({ ...prev, calorieGoal: validateCalorieGoal(val, customCalorieGoal) }));
                    }}
                    options={["Low Calorie (Weight Loss)","Balanced Wellness","High Calorie (Weight Gain)","High Protein (Muscle)","Low Carb / Keto","Detox / Immunity", "Custom / Other"]}
                  />
                  {calorieGoal === "Custom / Other" && (
                    <input
                      style={{
                        ...inputSty,
                        marginTop: 6,
                        borderColor: touched.calorieGoal && errors.calorieGoal ? "#EF4444" : T.border
                      }}
                      value={customCalorieGoal}
                      onChange={e => {
                        setCustomCalorieGoal(e.target.value);
                        if (touched.calorieGoal) setErrors(prev => ({ ...prev, calorieGoal: validateCalorieGoal("Custom / Other", e.target.value) }));
                      }}
                      onBlur={() => handleBlur("calorieGoal")}
                      placeholder="Enter custom calorie / health goal"
                    />
                  )}
                  {touched.calorieGoal && errors.calorieGoal && (
                    <p className="text-[11px] font-bold text-red-500 flex items-center gap-1 mt-1">
                      <FiAlertTriangle size={11} className="shrink-0" /> {errors.calorieGoal}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="text-[10px] font-black uppercase tracking-[0.9px] block mb-1.5"
                  style={{ color:T.muted }}>Target Daily Calories (kcal) <span className="text-red-500">*</span></label>
                <input
                  type="number"
                  style={{
                    ...inputSty,
                    borderColor: touched.totalCal && errors.totalCal ? "#EF4444" : T.border,
                    background: touched.totalCal && errors.totalCal ? "rgba(254,242,242,0.6)" : "rgba(255,255,255,0.9)"
                  }}
                  value={totalCal}
                  onChange={e => {
                    setTotalCal(e.target.value);
                    if (touched.totalCal) setErrors(prev => ({ ...prev, totalCal: validateTotalCal(e.target.value) }));
                  }}
                  onBlur={() => handleBlur("totalCal")}
                  placeholder="e.g. 1800"
                />
                {touched.totalCal && errors.totalCal && (
                  <p className="text-[11px] font-bold text-red-500 flex items-center gap-1 mt-1">
                    <FiAlertTriangle size={11} className="shrink-0" /> {errors.totalCal}
                  </p>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[10px] font-black uppercase tracking-[0.9px]"
                    style={{ color:T.muted }}>Plan Description / Overview</label>
                  <div className="flex items-center gap-2">
                    {description && (
                      <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                        ✓ Generated
                      </span>
                    )}
                    <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                      type="button" onClick={handleAiAutofill} disabled={aiLoading}
                      className="h-6 px-2.5 rounded-full text-white text-[10px] font-bold flex items-center gap-1 border-none cursor-pointer shadow-sm"
                      style={{ background: "linear-gradient(135deg,#F29A0C,#E97818)", boxShadow: "0 2px 6px rgba(242,154,12,0.3)" }}>
                      {aiLoading ? (
                        <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: "linear" }}>
                          <FiRefreshCw size={10}/>
                        </motion.div>
                      ) : (
                        <FiZap size={10}/>
                      )}
                      <span>{aiLoading ? "Generating..." : "✨ Generate Overview & Menu with AI"}</span>
                    </motion.button>
                  </div>
                </div>
                <textarea
                  rows={3}
                  style={{
                    ...inputSty,
                    resize:"none",
                    borderColor: touched.description && errors.description ? "#EF4444" : T.border
                  }}
                  value={description}
                  onChange={e => {
                    setDescription(e.target.value);
                    if (touched.description) setErrors(prev => ({ ...prev, description: validateDescription(e.target.value) }));
                  }}
                  onBlur={() => handleBlur("description")}
                  placeholder="Click 'Generate Overview & Menu with AI' above or enter clinical/Ayurvedic notes here..."
                />
                {touched.description && errors.description && (
                  <p className="text-[11px] font-bold text-red-500 flex items-center gap-1 mt-1">
                    <FiAlertTriangle size={11} className="shrink-0" /> {errors.description}
                  </p>
                )}
              </div>
            </div>
          )}

          {activeTab === "meals" && (
            <div>
              {/* Day selection pills */}
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div className="flex gap-1.5 overflow-x-auto pb-1" style={{ scrollbarWidth:"none" }}>
                  {DAYS.map((day, idx) => (
                    <button key={day} type="button" onClick={() => setActiveDayIdx(idx)}
                      className="px-3.5 py-1.5 rounded-[12px] text-[11px] font-bold border-none cursor-pointer flex items-center gap-1.5"
                      style={activeDayIdx === idx
                        ? { background:`linear-gradient(135deg,${T.olive},${T.olive2})`, color:"#fff", boxShadow:`0 4px 12px rgba(107,138,90,0.3)` }
                        : { background:"rgba(239,229,216,0.5)", color:T.mid }}>
                      {day}
                      <span className="text-[9px] px-1.5 py-0.2 rounded-full" style={{ background:"rgba(0,0,0,0.15)" }}>
                        {schedule[day]?.length || 0}
                      </span>
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => addCustomMealRow(currentDayName)}
                    className="px-3 py-1.5 rounded-full text-[11px] font-bold border cursor-pointer flex items-center gap-1"
                    style={{ background:"rgba(242,154,12,0.1)", color:T.orange, borderColor:"rgba(242,154,12,0.3)" }}>
                    <FiPlus size={12}/> Add Custom Meal Slot
                  </button>
                  <button type="button" onClick={() => copyDayToAll(currentDayName)}
                    className="px-3 py-1.5 rounded-full text-[10px] font-bold border cursor-pointer flex items-center gap-1"
                    style={{ background:"rgba(107,138,90,0.1)", color:T.olive, borderColor:"rgba(107,138,90,0.2)" }}>
                    <FiCopy size={11}/> Copy {currentDayName} to All 7 Days
                  </button>
                </div>
              </div>

              {/* Meal slots for active day */}
              {(!schedule[currentDayName] || schedule[currentDayName].length === 0) ? (
                <div className="p-8 text-center rounded-[20px] border border-dashed my-3"
                  style={{ borderColor: "rgba(239,229,216,0.9)", background: "rgba(255,255,255,0.7)" }}>
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
                    style={{ background: "rgba(242,154,12,0.12)" }}>
                    <FiZap size={22} style={{ color: T.orange }}/>
                  </div>
                  <p className="font-bold text-[14px] text-[#1F1F1F]">No Meals for {currentDayName}</p>
                  <p className="text-[12px] text-[#777] mt-1 mb-4">
                    Click below to generate 7-day personalized {dietType} ({region}) meals tailored to {totalCal} kcal target with AI.
                  </p>
                  <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                    type="button" onClick={handleAiAutofill} disabled={aiLoading}
                    className="px-6 py-2.5 rounded-full text-white text-[12px] font-bold border-none cursor-pointer inline-flex items-center gap-2 shadow-md"
                    style={{ background: "linear-gradient(135deg,#F29A0C,#E97818)" }}>
                    {aiLoading ? (
                      <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: "linear" }}>
                        <FiRefreshCw size={13}/>
                      </motion.div>
                    ) : (
                      <FiZap size={13}/>
                    )}
                    <span>{aiLoading ? "Generating 7-Day Menu..." : "⚡ Generate 7-Day Meals with AI"}</span>
                  </motion.button>
                </div>
              ) : (
                <div className="flex flex-col gap-3.5 max-h-[50vh] overflow-y-auto pr-1" style={{ scrollbarWidth:"none" }}>
                  {(() => {
                    const currentDayMeals = schedule[currentDayName] || [];
                    const timeCountsOnDay = {};
                    currentDayMeals.forEach(m => {
                      const norm = normalizeMealTime(m.time);
                      if (norm) timeCountsOnDay[norm] = (timeCountsOnDay[norm] || 0) + 1;
                    });
                    const duplicateMealIdsOnDay = new Set();
                    currentDayMeals.forEach(m => {
                      const norm = normalizeMealTime(m.time);
                      if (norm && timeCountsOnDay[norm] > 1) {
                        duplicateMealIdsOnDay.add(m.id);
                      }
                    });

                    return (
                      <>
                        {duplicateMealIdsOnDay.size > 0 && (
                          <div className="p-3 rounded-[14px] mb-2 flex items-center gap-2 border shadow-xs"
                            style={{ background: "rgba(254,242,242,0.92)", borderColor: "rgba(239,68,68,0.35)", color: "#DC2626" }}>
                            <FiAlertTriangle size={15} className="shrink-0 text-red-500" />
                            <span className="text-[11.5px] font-bold">
                              Duplicate meal timings detected on {currentDayName}. Each meal must be scheduled at a distinct time (e.g. 8:00 AM Breakfast and 11:00 AM Snack).
                            </span>
                          </div>
                        )}

                        {currentDayMeals.map((m, slotIdx) => {
                          const isSlotErr = !m.mealSlot?.trim() || !/^[a-zA-Z0-9\s.'\-()&/]+$/.test(m.mealSlot.trim()) || m.mealSlot.trim().length < 2;
                          const isNameErr = !m.mealName?.trim() || !/^[a-zA-Z0-9\s.'\-(),&/]+$/.test(m.mealName.trim()) || m.mealName.trim().length < 2;
                          const isCalErr = isNaN(Number(m.calories)) || Number(m.calories) < 0 || Number(m.calories) > 5000;
                          const isProtErr = isNaN(Number(m.protein)) || Number(m.protein) < 0 || Number(m.protein) > 500;
                          const isCarbErr = isNaN(Number(m.carbs)) || Number(m.carbs) < 0 || Number(m.carbs) > 500;
                          const isFatErr = isNaN(Number(m.fat)) || Number(m.fat) < 0 || Number(m.fat) > 500;
                          const isTimeEmpty = !m.time?.trim();
                          const isDuplicateTime = duplicateMealIdsOnDay.has(m.id);
                          const isTimeErr = isTimeEmpty || isDuplicateTime;

                          return (
                            <div key={m.id} className="p-3.5 rounded-[16px] border relative"
                              style={{
                                background:"#fff",
                                borderColor: (isSlotErr || isNameErr || isCalErr || isTimeErr) ? "rgba(239,68,68,0.45)" : "rgba(239,229,216,0.8)",
                                boxShadow:"0 2px 8px rgba(0,0,0,0.03)"
                              }}>
                              <div className="ad-wrap flex items-center justify-between gap-2 mb-2">
                                <div className="flex items-center gap-2">
                                  <span className="text-[10px] font-black px-2 py-0.5 rounded-md" style={{ background:"rgba(107,138,90,0.1)", color:T.olive }}>
                                    #{slotIdx + 1}
                                  </span>
                                  <div>
                                    <input
                                      style={{
                                        ...inputSty,
                                        width:180,
                                        fontWeight:"bold",
                                        borderColor: isSlotErr ? "#EF4444" : T.border,
                                        background: isSlotErr ? "rgba(254,242,242,0.6)" : "rgba(255,255,255,0.9)"
                                      }}
                                      value={m.mealSlot}
                                      onChange={e => updateMealField(currentDayName, m.id, "mealSlot", e.target.value)}
                                      placeholder="Slot (e.g. Morning Snack)"
                                    />
                                  </div>
                                </div>
                                <div className="flex items-center gap-3">
                                  <div className="flex items-center gap-1">
                                    <span className="text-[10px] font-bold flex items-center gap-0.5" style={{ color: isTimeErr ? "#EF4444" : T.muted }}>
                                      <FiClock size={11} style={{ color: isTimeErr ? "#EF4444" : undefined }}/> Time:
                                    </span>
                                    <input
                                      style={{
                                        ...inputSty,
                                        width:115,
                                        textAlign:"center",
                                        borderColor: isTimeErr ? "#EF4444" : T.border,
                                        background: isDuplicateTime ? "rgba(254,242,242,0.85)" : isTimeEmpty ? "rgba(254,242,242,0.6)" : "rgba(255,255,255,0.9)",
                                        color: isDuplicateTime ? "#DC2626" : T.dark,
                                        fontWeight: isDuplicateTime ? "700" : "500"
                                      }}
                                      value={m.time}
                                      onChange={e => updateMealField(currentDayName, m.id, "time", e.target.value)}
                                      placeholder="08:00 AM"
                                    />
                                  </div>
                                  <button type="button" onClick={() => removeMealRow(currentDayName, m.id)}
                                    className="w-7 h-7 rounded-full flex items-center justify-center border-none cursor-pointer"
                                    style={{ background:"rgba(226,75,74,0.1)", color:T.red }} title="Remove Meal Slot">
                                    <FiTrash2 size={12}/>
                                  </button>
                                </div>
                              </div>

                              {isDuplicateTime && (
                                <p className="text-[10.5px] font-bold text-red-500 flex items-center gap-1 mb-2">
                                  <FiAlertTriangle size={11} className="shrink-0" />
                                  Duplicate timing ({m.time}): Another meal on {currentDayName} is scheduled at this same time.
                                </p>
                              )}

                              <div className="flex flex-col gap-2">
                                <div>
                                  <input
                                    style={{
                                      ...inputSty,
                                      fontWeight:"bold",
                                      borderColor: isNameErr ? "#EF4444" : T.border,
                                      background: isNameErr ? "rgba(254,242,242,0.6)" : "rgba(255,255,255,0.9)"
                                    }}
                                    value={m.mealName}
                                    onChange={e => updateMealField(currentDayName, m.id, "mealName", e.target.value)}
                                    placeholder={`Enter meal name for ${m.mealSlot || 'slot'}...`}
                                  />
                                  {isNameErr && m.mealName?.trim() && (
                                    <p className="text-[10.5px] font-bold text-red-500 flex items-center gap-1 mt-1">
                                      <FiAlertTriangle size={10} className="shrink-0" /> Only letters, numbers, spaces, and commas/hyphens allowed (min 2 chars).
                                    </p>
                                  )}
                                </div>
                          <input style={{ ...inputSty, background:"rgba(245,250,242,0.7)" }} value={m.description || m.notes || ""}
                            onChange={e => {
                              updateMealField(currentDayName, m.id, "description", e.target.value);
                              updateMealField(currentDayName, m.id, "notes", e.target.value);
                            }}
                            placeholder={`Enter meal description & recipe guidance for ${m.mealSlot || 'slot'}...`} />
                          <div className="ad-2col grid grid-cols-4 gap-2">
                            <div>
                              <span className="text-[9px] font-bold block mb-0.5" style={{ color: isCalErr ? "#EF4444" : T.muted }}>Calories (kcal)</span>
                              <input
                                type="number"
                                style={{
                                  ...inputSty,
                                  borderColor: isCalErr ? "#EF4444" : T.border,
                                  background: isCalErr ? "rgba(254,242,242,0.6)" : "rgba(255,255,255,0.9)"
                                }}
                                value={m.calories}
                                onChange={e => updateMealField(currentDayName, m.id, "calories", e.target.value)}
                              />
                            </div>
                            <div>
                              <span className="text-[9px] font-bold block mb-0.5" style={{ color: isProtErr ? "#EF4444" : T.muted }}>Protein (g)</span>
                              <input
                                type="number"
                                style={{
                                  ...inputSty,
                                  borderColor: isProtErr ? "#EF4444" : T.border,
                                  background: isProtErr ? "rgba(254,242,242,0.6)" : "rgba(255,255,255,0.9)"
                                }}
                                value={m.protein}
                                onChange={e => updateMealField(currentDayName, m.id, "protein", e.target.value)}
                              />
                            </div>
                            <div>
                              <span className="text-[9px] font-bold block mb-0.5" style={{ color: isCarbErr ? "#EF4444" : T.muted }}>Carbs (g)</span>
                              <input
                                type="number"
                                style={{
                                  ...inputSty,
                                  borderColor: isCarbErr ? "#EF4444" : T.border,
                                  background: isCarbErr ? "rgba(254,242,242,0.6)" : "rgba(255,255,255,0.9)"
                                }}
                                value={m.carbs}
                                onChange={e => updateMealField(currentDayName, m.id, "carbs", e.target.value)}
                              />
                            </div>
                            <div>
                              <span className="text-[9px] font-bold block mb-0.5" style={{ color: isFatErr ? "#EF4444" : T.muted }}>Fat (g)</span>
                              <input
                                type="number"
                                style={{
                                  ...inputSty,
                                  borderColor: isFatErr ? "#EF4444" : T.border,
                                  background: isFatErr ? "rgba(254,242,242,0.6)" : "rgba(255,255,255,0.9)"
                                }}
                                value={m.fat}
                                onChange={e => updateMealField(currentDayName, m.id, "fat", e.target.value)}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </>
              );
            })()}
          </div>
        )}
      </div>
    )}

          {/* Modal Actions */}
          <div className="flex gap-2.5 mt-6 pt-4 border-t" style={{ borderColor:"rgba(239,229,216,0.6)" }}>
            {activeTab === "details" ? (
              hasMeals ? (
                <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}
                  type="button" onClick={() => {
                    if (!validateAllDetails()) return;
                    setActiveTab("meals");
                  }}
                  className="flex-1 h-11 rounded-full text-white font-bold text-[13px] border-none cursor-pointer flex items-center justify-center gap-2 shadow-lg"
                  style={{
                    background: `linear-gradient(135deg,${T.olive},${T.olive2})`,
                    boxShadow: "0 6px 20px rgba(107,138,90,0.35)",
                    fontFamily: "'Inter',sans-serif"
                  }}>
                  <span>Next: Customize 7-Day Weekly Meals & Timings ({totalMealsCount} Meals Ready) ➔</span>
                </motion.button>
              ) : (
                <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}
                  type="button" onClick={handleAiAutofill} disabled={aiLoading}
                  className="flex-1 h-11 rounded-full text-white font-bold text-[13px] border-none cursor-pointer flex items-center justify-center gap-2 shadow-lg"
                  style={{
                    background: "linear-gradient(135deg,#F29A0C,#E97818)",
                    boxShadow: "0 6px 20px rgba(242,154,12,0.35)",
                    cursor: aiLoading ? "not-allowed" : "pointer"
                  }}>
                  {aiLoading ? (
                    <>
                      <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: "linear" }}>
                        <FiRefreshCw size={14}/>
                      </motion.div>
                      <span>Generating Plan Description & Menu with AI...</span>
                    </>
                  ) : (
                    <>
                      <FiZap size={14}/>
                      <span>⚡ Generate Plan Description & 7-Day Menu with AI</span>
                    </>
                  )}
                </motion.button>
              )
            ) : (
              <motion.button whileHover={{ y:-1 }} disabled={submitting} onClick={handleSave}
                className="flex-1 h-11 rounded-full text-white font-bold text-[13px] flex items-center justify-center gap-2 border-none cursor-pointer"
                style={{ background:`linear-gradient(135deg,${T.olive},${T.olive2})`,
                  boxShadow:`0 6px 18px rgba(107,138,90,0.38)`, fontFamily:"'Inter',sans-serif" }}>
                <FiCheckCircle size={14}/>
                {submitting ? "Saving All 7 Days..." : isEdit ? "Save 7-Day Plan" : "Create & Broadcast 7-Day Plan"}
              </motion.button>
            )}
            <motion.button whileHover={{ background:"#fff" }} onClick={onClose}
              className="h-11 px-5 rounded-full text-[13px] font-semibold cursor-pointer"
              style={{ background:"rgba(255,255,255,0.9)", border:`1.5px solid ${T.border}`,
                color:T.mid, fontFamily:"'Inter',sans-serif" }}>
              Cancel
            </motion.button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export default function DietPlanManagerPage() {
  const { tiers: dynamicTiers } = useDynamicPackages();
  const [plans, setPlans]       = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [search, setSearch]     = useState("");
  const [tierF, setTierF]       = useState("all");
  const [statusF, setStatusF]   = useState("all");
  const [drawerId, setDrawerID] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editPlan, setEditPlan] = useState(null);
  const [toast, setToast]       = useState(null);

  // User tracking states
  const [viewTab, setViewTab] = useState("plans"); // "plans" | "tracking"
  const [trackingData, setTrackingData] = useState([]);
  const [loadingTracking, setLoadingTracking] = useState(false);
  const [trackingSearch, setTrackingSearch] = useState("");
  const [trackingTierF, setTrackingTierF] = useState("all");
  const [trackingStatusF, setTrackingStatusF] = useState("all");

  const loadUserTracking = async () => {
    setLoadingTracking(true);
    try {
      const res = await dietPlanAPI.getUserTracking();
      if (res.data?.data) {
        setTrackingData(res.data.data);
      }
    } catch (e) {
      console.error("Failed to load user tracking data", e);
    } finally {
      setLoadingTracking(false);
    }
  };

  useEffect(() => {
    if (viewTab === "tracking") {
      loadUserTracking();
    }
  }, [viewTab]);

  const filteredTracking = useMemo(() => {
    return trackingData.filter(item => {
      // 1. Search filter
      const s = trackingSearch.toLowerCase();
      const matchesSearch = !s ||
        (item.fullName || "").toLowerCase().includes(s) ||
        (item.email || "").toLowerCase().includes(s) ||
        (item.matchedPlanName || "").toLowerCase().includes(s);

      // 2. Tier filter
      const matchesTier = trackingTierF === "all" ||
        item.tier.toLowerCase().includes(trackingTierF.toLowerCase());

      // 3. Status filter
      let matchesStatus = true;
      if (trackingStatusF === "completed") {
        matchesStatus = item.totalMeals > 0 && item.completedMeals === item.totalMeals;
      } else if (trackingStatusF === "partial") {
        matchesStatus = item.completedMeals > 0 && item.completedMeals < item.totalMeals;
      } else if (trackingStatusF === "not_started") {
        matchesStatus = item.completedMeals === 0;
      }

      return matchesSearch && matchesTier && matchesStatus;
    });
  }, [trackingData, trackingSearch, trackingTierF, trackingStatusF]);

  const trackingStats = useMemo(() => {
    const total = trackingData.length;
    const completed = trackingData.filter(item => item.totalMeals > 0 && item.completedMeals === item.totalMeals).length;
    const partial = trackingData.filter(item => item.completedMeals > 0 && item.completedMeals < item.totalMeals).length;
    const notStarted = trackingData.filter(item => item.completedMeals === 0 && item.totalMeals > 0).length;
    const noPlan = trackingData.filter(item => item.totalMeals === 0).length;

    const completedMealsToday = trackingData.reduce((sum, item) => sum + (item.completedMealsToday || 0), 0);
    const completedMealsWeek = trackingData.reduce((sum, item) => sum + (item.completedMealsWeek || 0), 0);
    const activeLoggers = trackingData.filter(item => item.completedMealsWeek > 0).length;

    const usersCompletedToday = trackingData.filter(item => item.totalMealsToday > 0 && item.completedMealsToday === item.totalMealsToday).length;
    const usersPartialToday = trackingData.filter(item => item.totalMealsToday > 0 && item.completedMealsToday > 0 && item.completedMealsToday < item.totalMealsToday).length;
    const usersCompletedWeek = completed;

    return { 
      total, 
      completed, 
      partial, 
      notStarted, 
      noPlan, 
      completedMealsToday, 
      completedMealsWeek, 
      activeLoggers,
      usersCompletedToday,
      usersPartialToday,
      usersCompletedWeek
    };
  }, [trackingData]);

  // Custom confirmation modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    confirmText: "Confirm",
    isDanger: false,
    onConfirm: null
  });

  const showToast = useCallback((msg) => {
    setToast(msg); setTimeout(() => setToast(null), 3200);
  }, []);

  const loadAdminPlans = async () => {
    try {
      const res = await dietPlanAPI.getAdminPlans();
      if (res.data?.data) {
        const normalized = res.data.data.map(p => formatPlanFromDb(p));
        setPlans(normalized);
      }
    } catch (e) {
      console.warn("Admin plans API load note:", e);
    } finally {
      setLoadingPlans(false);
    }
  };

  useEffect(() => {
    loadAdminPlans();
  }, []);

  const handleDuplicatePlan = async (planToDup) => {
    try {
      const compiledMeals = [];
      DAYS.forEach(day => {
        const dayMeals = planToDup.rawMeals?.filter(m => m.day === day) || [];
        dayMeals.forEach(m => {
          compiledMeals.push({
            day,
            mealSlot: m.mealSlot,
            mealName: m.mealName,
            description: m.description || m.Description || m.notes || m.Notes || "",
            time: m.time || "08:00 AM",
            calories: m.calories,
            protein: m.protein,
            carbs: m.carbs,
            fat: m.fat,
            prepTime: m.prepTime || "15 min",
            notes: m.description || m.Description || m.notes || m.Notes || "",
            tags: m.tags || "Healthy"
          });
        });
      });

      const payload = {
        name: `${planToDup.name} (Copy)`,
        tier: planToDup.tier,
        dietType: planToDup.dietType,
        region: planToDup.region,
        calorieGoal: planToDup.calorieGoal,
        totalCalories: planToDup.totalCal,
        description: planToDup.description || planToDup.Description || "",
        status: planToDup.status,
        meals: compiledMeals
      };

      await dietPlanAPI.saveAdminPlan(payload);
      showToast(`✓ Duplicated "${planToDup.name}" successfully!`);
      loadAdminPlans();
    } catch (e) {
      console.error("Duplicate plan error", e);
      showToast("Error duplicating plan");
    }
  };

  const handleToggleStatus = async (planToToggle) => {
    try {
      const nextStatus = planToToggle.status === "Published" ? "Draft" : "Published";
      const compiledMeals = [];
      DAYS.forEach(day => {
        const dayMeals = planToToggle.rawMeals?.filter(m => m.day === day) || [];
        dayMeals.forEach(m => {
          compiledMeals.push({
            day,
            mealSlot: m.mealSlot,
            mealName: m.mealName,
            description: m.description || m.Description || m.notes || m.Notes || "",
            time: m.time || "08:00 AM",
            calories: m.calories,
            protein: m.protein,
            carbs: m.carbs,
            fat: m.fat,
            prepTime: m.prepTime || "15 min",
            notes: m.description || m.Description || m.notes || m.Notes || "",
            tags: m.tags || "Healthy"
          });
        });
      });

      const payload = {
        id: planToToggle.id,
        name: planToToggle.name,
        tier: planToToggle.tier,
        dietType: planToToggle.dietType,
        region: planToToggle.region,
        calorieGoal: planToToggle.calorieGoal,
        totalCalories: planToToggle.totalCal,
        description: planToToggle.description || planToToggle.Description || "",
        status: nextStatus,
        meals: compiledMeals
      };

      await dietPlanAPI.saveAdminPlan(payload);
      showToast(`✓ Plan status updated to ${nextStatus}`);
      loadAdminPlans();
    } catch (e) {
      console.error("Toggle status error", e);
    }
  };

  const promptDeletePlan = (planId) => {
    const p = plans.find(x => x.id === planId);
    setConfirmModal({
      isOpen: true,
      title: "Delete Diet Plan?",
      message: `Are you sure you want to delete "${p?.name || 'this plan'}"? This action cannot be undone.`,
      confirmText: "Delete Plan",
      isDanger: true,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
        try {
          await dietPlanAPI.deleteAdminPlan(planId);
          showToast("✓ Diet plan deleted");
          loadAdminPlans();
        } catch (e) {
          console.error("Delete plan error", e);
          showToast("Error deleting plan");
        }
      }
    });
  };

  const promptClearAllPlans = () => {
    setConfirmModal({
      isOpen: true,
      title: "Delete ALL Diet Plans?",
      message: "Are you sure you want to permanently delete ALL diet plans from SQL Server? All cards will be cleared.",
      confirmText: "Wipe All Plans",
      isDanger: true,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
        try {
          await dietPlanAPI.clearAllPlans();
          showToast("✓ All diet plans deleted");
          loadAdminPlans();
        } catch (e) {
          console.error("Clear all error", e);
          showToast("Error clearing all plans");
        }
      }
    });
  };

  const [userSearch, setUserSearch] = useState("");
  const [userDietFilter, setUserDietFilter] = useState("all");

  const templatePlans = useMemo(() => plans.filter(p => !p.tier || !p.tier.toLowerCase().startsWith("user:")), [plans]);
  const userGeneratedPlans = useMemo(() => plans.filter(p => p.tier && p.tier.toLowerCase().startsWith("user:")), [plans]);

  // ── Responsive Dynamic Screen Detector (Width columns & Height rows)
  const [cardCols, setCardCols] = useState(() => {
    if (typeof window === "undefined") return 3;
    if (window.innerWidth >= 1600) return 4;
    if (window.innerWidth >= 1100) return 3;
    if (window.innerWidth >= 640) return 2;
    return 1;
  });

  const [screenRows, setScreenRows] = useState(() => {
    if (typeof window === "undefined") return 2;
    if (window.innerHeight >= 950) return 3;
    if (window.innerHeight >= 700) return 2;
    return 1;
  });

  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      if (w >= 1600) setCardCols(4);
      else if (w >= 1100) setCardCols(3);
      else if (w >= 640) setCardCols(2);
      else setCardCols(1);

      if (h >= 950) setScreenRows(3);
      else if (h >= 700) setScreenRows(2);
      else setScreenRows(1);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Plans pagination (Default: "auto" for dynamic screen-fit)
  const [planPage, setPlanPage] = useState(1);
  const [planPageSize, setPlanPageSize] = useState("auto");

  // Tracking pagination (Default: "auto" for dynamic screen-fit)
  const [trackingPage, setTrackingPage] = useState(1);
  const [trackingPageSize, setTrackingPageSize] = useState("auto");

  useEffect(() => {
    setPlanPage(1);
  }, [search, tierF, statusF]);

  useEffect(() => {
    setTrackingPage(1);
  }, [trackingSearch, trackingTierF, trackingStatusF]);

  const filteredTemplates = useMemo(() => templatePlans.filter(p => {
    const q = search.toLowerCase();
    return (
      (!q || p.name.toLowerCase().includes(q) || p.tier.toLowerCase().includes(q)) &&
      (tierF   === "all" || p.tier   === tierF) &&
      (statusF === "all" || p.status === statusF)
    );
  }), [templatePlans, search, tierF, statusF]);

  const filteredUserPlans = useMemo(() => userGeneratedPlans.filter(p => {
    const q = userSearch.toLowerCase();
    const matchesQuery = !q || p.name.toLowerCase().includes(q) || p.tier.toLowerCase().includes(q) || (p.region && p.region.toLowerCase().includes(q));
    const matchesDiet = userDietFilter === "all" || (p.dietType && p.dietType.toLowerCase() === userDietFilter.toLowerCase());
    return matchesQuery && matchesDiet;
  }), [userGeneratedPlans, userSearch, userDietFilter]);

  const dynamicPlanPageSize = useMemo(() => {
    if (planPageSize === "all") {
      return filteredTemplates.length > 0 ? filteredTemplates.length : 50;
    }
    if (planPageSize === "auto") {
      return Math.max(2, cardCols * screenRows);
    }
    return Number(planPageSize);
  }, [planPageSize, cardCols, screenRows, filteredTemplates.length]);

  const dynamicTrackingPageSize = useMemo(() => {
    if (trackingPageSize === "all") {
      return filteredTracking.length > 0 ? filteredTracking.length : 50;
    }
    if (trackingPageSize === "auto") {
      return screenRows >= 3 ? 15 : 10;
    }
    return Number(trackingPageSize);
  }, [trackingPageSize, screenRows, filteredTracking.length]);

  const planPageSizeOptions = useMemo(() => [
    { label: `Auto Fit (${cardCols * screenRows} cards)`, value: "auto" },
    { label: "Show 6", value: 6 },
    { label: "Show 12", value: 12 },
    { label: "Show 24", value: 24 },
    { label: `Show All (${filteredTemplates.length})`, value: "all" },
  ], [cardCols, screenRows, filteredTemplates.length]);

  const trackingPageSizeOptions = useMemo(() => [
    { label: `Auto Fit (${screenRows >= 3 ? 15 : 10} rows)`, value: "auto" },
    { label: "10 per page", value: 10 },
    { label: "20 per page", value: 20 },
    { label: "50 per page", value: 50 },
    { label: `Show All (${filteredTracking.length})`, value: "all" },
  ], [screenRows, filteredTracking.length]);

  // Plan pages calculation: Page 1 reserves 1 slot for "New Diet Plan" card + (dynamicPlanPageSize - 1) diet cards
  // Subsequent pages (Page 2, 3...) show dynamicPlanPageSize diet cards with NO create card.
  const totalPlanPages = useMemo(() => {
    if (planPageSize === "all") return 1;
    const page1DietSlots = Math.max(1, dynamicPlanPageSize - 1);
    if (filteredTemplates.length <= page1DietSlots) return 1;
    const remaining = filteredTemplates.length - page1DietSlots;
    return 1 + Math.ceil(remaining / dynamicPlanPageSize);
  }, [filteredTemplates.length, dynamicPlanPageSize, planPageSize]);

  const paginatedPlans = useMemo(() => {
    if (planPageSize === "all") return filteredTemplates;
    const page1DietSlots = Math.max(1, dynamicPlanPageSize - 1);
    if (planPage === 1) {
      return filteredTemplates.slice(0, page1DietSlots);
    }
    const start = page1DietSlots + (planPage - 2) * dynamicPlanPageSize;
    return filteredTemplates.slice(start, start + dynamicPlanPageSize);
  }, [filteredTemplates, planPage, dynamicPlanPageSize, planPageSize]);

  const planStartIndex = useMemo(() => {
    if (filteredTemplates.length === 0) return 0;
    if (planPage === 1) return 1;
    const page1DietSlots = Math.max(1, dynamicPlanPageSize - 1);
    return page1DietSlots + (planPage - 2) * dynamicPlanPageSize + 1;
  }, [filteredTemplates.length, planPage, dynamicPlanPageSize]);

  const planEndIndex = useMemo(() => {
    if (filteredTemplates.length === 0) return 0;
    if (planPageSize === "all") return filteredTemplates.length;
    return Math.min(planStartIndex + paginatedPlans.length - 1, filteredTemplates.length);
  }, [filteredTemplates.length, planPageSize, planStartIndex, paginatedPlans.length]);

  const totalTrackingPages = Math.max(1, Math.ceil(filteredTracking.length / dynamicTrackingPageSize));
  const paginatedTracking = useMemo(() => {
    if (trackingPageSize === "all") return filteredTracking;
    const start = (trackingPage - 1) * dynamicTrackingPageSize;
    return filteredTracking.slice(start, start + dynamicTrackingPageSize);
  }, [filteredTracking, trackingPage, dynamicTrackingPageSize, trackingPageSize]);

  const drawerPlan = drawerId ? plans.find(p => p.id === drawerId) : null;

  const totalSubscribers = plans.reduce((a,p) => a + p.subscribers, 0);
  const publishedCount   = templatePlans.filter(p => p.status === "Published").length;
  const avgCal           = templatePlans.length > 0 ? Math.round(templatePlans.reduce((a,p) => a + p.totalCal, 0) / templatePlans.length) : 0;

  // Real dynamic counts for Tiers and Meals
  const distinctTiersCount = useMemo(() => {
    return new Set(plans.map(p => p.tier).filter(Boolean)).size;
  }, [plans]);

  const totalMealsConfigured = useMemo(() => {
    return plans.reduce((sum, p) => {
      if (p.rawMeals && p.rawMeals.length > 0) return sum + p.rawMeals.length;
      if (p.days && Array.isArray(p.days)) {
        return sum + p.days.reduce((dSum, d) => dSum + (d.meals?.length || 0), 0);
      }
      return sum;
    }, 0);
  }, [plans]);

  const avgMealsPerPlan = useMemo(() => {
    if (plans.length === 0) return 0;
    return Math.round(totalMealsConfigured / plans.length);
  }, [plans.length, totalMealsConfigured]);

  if (loadingPlans && plans.length === 0) {
    return (
      <AdminDashboardLayout activeNav="diet">
        <BrandLoader 
          message="Loading Diet Plan Manager..." 
          subMessage="Fetching meal schedules, nutrition plans & user tracking" 
        />
      </AdminDashboardLayout>
    );
  }

  return (
    <AdminDashboardLayout activeNav="diet">
      <div style={{ fontFamily:"'Inter',sans-serif" }}>

        {/* Ambient orbs */}
        <motion.div animate={{ y:[0,-18,0],x:[0,14,0] }}
          transition={{ duration:14,repeat:Infinity,ease:"easeInOut" }}
          className="fixed w-[460px] h-[460px] rounded-full pointer-events-none"
          style={{ background:"radial-gradient(circle,rgba(107,138,90,0.07),transparent 70%)",
            filter:"blur(80px)", top:-140, right:-80, zIndex:0 }}/>
        <motion.div animate={{ y:[0,14,0],x:[0,-10,0] }}
          transition={{ duration:17,repeat:Infinity,ease:"easeInOut" }}
          className="fixed w-[340px] h-[340px] rounded-full pointer-events-none"
          style={{ background:"radial-gradient(circle,rgba(242,154,12,0.06),transparent 70%)",
            filter:"blur(80px)", bottom:-80, left:40, zIndex:0 }}/>

        <div className="relative z-10 w-full min-w-0">

          {/* Topbar */}
          <motion.div initial={{ opacity:0,y:-16 }} animate={{ opacity:1,y:0 }}
            className="ad-toolbar flex items-center justify-between mb-6">
            <div>
              <h1 className="font-black text-[#1F1F1F]"
                style={{ fontFamily:"'Poppins',sans-serif", fontSize:26, letterSpacing:"-1px" }}>
                Diet Plan Manager
              </h1>
              <p className="text-[12px] mt-1" style={{ color:T.muted }}>
                {templatePlans.length} templates · {userGeneratedPlans.length} user AI plans · {totalSubscribers.toLocaleString()} total subscribers
              </p>
            </div>
            <div className="ad-wrap flex gap-2">
              <motion.button whileHover={{ y:-1 }} onClick={async () => {
                  try {
                    await dietPlanAPI.cleanupDuplicates();
                    showToast("✓ Cleaned duplicate plans");
                    loadAdminPlans();
                  } catch (e) {
                    console.error("Cleanup note", e);
                  }
                }}
                className="h-9 px-4 rounded-full text-[12px] font-semibold flex items-center gap-1.5 cursor-pointer"
                style={{ background:"rgba(242,154,12,0.08)", border:"1px solid rgba(242,154,12,0.25)",
                  color:T.orange, fontFamily:"'Inter',sans-serif" }}>
                <FiTrash2 size={13}/> Clean Duplicates
              </motion.button>
              <motion.button whileHover={{ y:-1 }} onClick={promptClearAllPlans}
                className="h-9 px-4 rounded-full text-[12px] font-semibold flex items-center gap-1.5 cursor-pointer"
                style={{ background:"rgba(226,75,74,0.12)", border:"1px solid rgba(226,75,74,0.3)",
                  color:T.red, fontFamily:"'Inter',sans-serif" }}>
                <FiTrash2 size={13}/> Delete All Plans
              </motion.button>
              <motion.button whileHover={{ y:-1 }} onClick={() => {
                  if (plans.length > 0) {
                    exportPlanAsFile(plans[0]);
                    showToast("✓ Exported top plan as TXT");
                  } else {
                    showToast("No plans available to export");
                  }
                }}
                className="h-9 px-4 rounded-full text-[12px] font-semibold flex items-center gap-1.5"
                style={{ background:"rgba(255,255,255,0.9)", border:`1.5px solid ${T.border}`,
                  color:T.mid, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                <FiDownload size={13}/> Export Report
              </motion.button>
              <motion.button whileHover={{ y:-2 }} whileTap={{ scale:0.97 }}
                onClick={() => setShowCreate(true)}
                className="h-9 px-5 rounded-full text-white text-[12px] font-bold flex items-center gap-1.5"
                style={{ background:`linear-gradient(135deg,${T.olive},${T.olive2})`,
                  boxShadow:`0 5px 16px rgba(107,138,90,0.38)`, border:"none",
                  cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                <FiPlus size={13}/> New Plan
              </motion.button>
            </div>
          </motion.div>

          {/* Sub-Tabs: Plan Templates vs User Diet Plans vs User Progress Tracker */}
          <div className="ad-scroll flex border-b border-[rgba(239,229,216,0.5)] mb-6 gap-6">
            <button
              onClick={() => setViewTab("plans")}
              className="pb-3 text-[14px] font-bold relative cursor-pointer transition-all duration-300"
              style={{
                color: viewTab === "plans" ? T.olive : T.muted,
                fontFamily: "'Poppins', sans-serif",
                background: "transparent",
                border: "none"
              }}
            >
              <span className="flex items-center gap-1.5">
                <FiLayers size={14} /> Plan Templates ({templatePlans.length})
              </span>
              {viewTab === "plans" && (
                <motion.div
                  layoutId="activeTabUnderline"
                  className="absolute bottom-0 left-0 right-0 h-[3px] rounded-t-full"
                  style={{ background: T.olive }}
                />
              )}
            </button>
            <button
              onClick={() => setViewTab("user-plans")}
              className="pb-3 text-[14px] font-bold relative cursor-pointer transition-all duration-300"
              style={{
                color: viewTab === "user-plans" ? T.olive : T.muted,
                fontFamily: "'Poppins', sans-serif",
                background: "transparent",
                border: "none"
              }}
            >
              <span className="flex items-center gap-1.5">
                <FiCalendar size={14} /> User Diet Plans ({userGeneratedPlans.length})
              </span>
              {viewTab === "user-plans" && (
                <motion.div
                  layoutId="activeTabUnderline"
                  className="absolute bottom-0 left-0 right-0 h-[3px] rounded-t-full"
                  style={{ background: T.olive }}
                />
              )}
            </button>
            <button
              onClick={() => setViewTab("tracking")}
              className="pb-3 text-[14px] font-bold relative cursor-pointer transition-all duration-300"
              style={{
                color: viewTab === "tracking" ? T.olive : T.muted,
                fontFamily: "'Poppins', sans-serif",
                background: "transparent",
                border: "none"
              }}
            >
              <span className="flex items-center gap-1.5">
                <FiUsers size={14} /> User Progress Tracker
              </span>
              {viewTab === "tracking" && (
                <motion.div
                  layoutId="activeTabUnderline"
                  className="absolute bottom-0 left-0 right-0 h-[3px] rounded-t-full"
                  style={{ background: T.olive }}
                />
              )}
            </button>
          </div>

          {viewTab === "plans" && (
            <>
              {/* Stat strip */}
              <div className="ad-kpi grid grid-cols-5 gap-3 mb-5">
                <StatCard icon={FiCalendar} value={templatePlans.length} label="Template Plans"
                  trend={`${publishedCount} published`}
                  iconBg={`linear-gradient(135deg,${T.olive},${T.olive2})`}
                  accentColor={`linear-gradient(90deg,${T.olive},${T.olive2})`}
                  cardBg="linear-gradient(145deg,#F5FAF2,#F9FDF5)" delay={0.06}/>
                <StatCard icon={FiCheckCircle} value={publishedCount} label="Active Plans"
                  trend="Live & accessible"
                  iconBg={`linear-gradient(135deg,${T.orange},${T.orange2})`}
                  accentColor={`linear-gradient(90deg,${T.orange},${T.orange2})`}
                  cardBg="linear-gradient(145deg,#FFF4E8,#FFF8EE)" delay={0.1}/>
                <StatCard icon={FiUsers} value={distinctTiersCount} label="Tiers Covered"
                  trend={distinctTiersCount > 0 ? `${distinctTiersCount} active tier${distinctTiersCount !== 1 ? "s" : ""}` : "No tiers yet"}
                  iconBg={`linear-gradient(135deg,${T.olive},${T.olive2})`}
                  accentColor="#534AB7" delay={0.14}/>
                <StatCard icon={FiClock} value={totalMealsConfigured} label="Total Meals"
                  trend={templatePlans.length > 0 ? `${avgMealsPerPlan} meals/plan avg` : "0 meals configured"}
                  iconBg={`linear-gradient(135deg,${T.orange},${T.orange2})`}
                  accentColor={T.blue} delay={0.18}/>
                <StatCard icon={FiTrendingUp} value={`${avgCal}`} label="Avg Daily kcal"
                  trend="Across all templates"
                  iconBg={`linear-gradient(135deg,${T.olive},${T.olive2})`}
                  accentColor={T.green} delay={0.22}/>
              </div>

              {/* Filter bar with CustomSelect */}
              <motion.div initial={{ opacity:0,y:12 }} animate={{ opacity:1,y:0 }} transition={{ delay:0.1 }}
                className="ad-filters flex items-center gap-3 mb-5 flex-wrap">
                <div className="ad-filters-full flex items-center gap-2 rounded-[14px] px-3.5 py-2.5"
                  style={{ background:"rgba(255,255,255,0.82)", border:`1px solid ${T.border}` }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#CCC" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                  <input value={search} onChange={e => setSearch(e.target.value)}
                    placeholder="Search templates…"
                    className="bg-transparent border-none outline-none text-[13px] text-[#1F1F1F]"
                    style={{ fontFamily:"'Inter',sans-serif", width:160 }}/>
                </div>
                <div style={{ width: 160 }}>
                  <CustomSelect value={tierF} onChange={setTierF}
                    options={[{ label:"All Tiers", value:"all" }, ...dynamicTiers.map(t => ({ label:`${t} Tier`, value:t }))]} />
                </div>
                <div style={{ width: 160 }}>
                  <CustomSelect value={statusF} onChange={setStatusF}
                    options={[
                      { label:"All Statuses", value:"all" },
                      { label:"Published", value:"Published" },
                      { label:"Draft", value:"Draft" },
                      { label:"Archived", value:"Archived" }
                    ]} />
                </div>
                <div style={{ width: 160 }}>
                  <CustomSelect value={planPageSize} onChange={val => {
                    setPlanPageSize(val === "all" || val === "auto" ? val : Number(val));
                    setPlanPage(1);
                  }}
                    options={planPageSizeOptions} />
                </div>
                <span className="ad-filters-full text-[11px] ml-auto font-bold" style={{ color:T.muted }}>
                  {filteredTemplates.length} template{filteredTemplates.length!==1?"s":""}
                </span>
              </motion.div>

              {/* Plans grid */}
              <div className="grid gap-4"
                style={{ gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))" }}>
                
                {/* 1. Add new plan card: ONLY AT FIRST PLACE (Slot 1) ON PAGE 1 */}
                {planPage === 1 && (
                  <motion.div initial={{ opacity:0,y:14 }} animate={{ opacity:1,y:0 }}
                    whileHover={{ y:-5, boxShadow:"0 22px 55px rgba(0,0,0,0.08)", borderColor:"rgba(107,138,90,0.35)" }}
                    onClick={() => setShowCreate(true)}
                    className="rounded-[24px] flex flex-col items-center justify-center gap-3 cursor-pointer min-h-[260px]"
                    style={{ background:"rgba(255,255,255,0.5)", border:`2px dashed rgba(107,138,90,0.3)`,
                      transition:"all 0.28s" }}>
                    <div className="w-12 h-12 rounded-[16px] flex items-center justify-center"
                      style={{ background:`linear-gradient(135deg,${T.olive},${T.olive2})`,
                        boxShadow:`0 8px 24px rgba(107,138,90,0.38)` }}>
                      <FiPlus size={20} style={{ color:"#fff" }}/>
                    </div>
                    <div className="text-center">
                      <p className="font-black text-[14px] text-[#1F1F1F]"
                        style={{ fontFamily:"'Poppins',sans-serif" }}>New Diet Template</p>
                      <p className="text-[12px] mt-1" style={{ color:T.muted }}>
                        Create an official 7-day plan
                      </p>
                    </div>
                  </motion.div>
                )}

                {/* 2. Diet Plan Cards */}
                {paginatedPlans.map((plan, i) => (
                  <PlanCard key={plan.id} plan={plan} delay={i * 0.04}
                    onOpen={setDrawerID}
                    onEdit={(id) => { setEditPlan(plans.find(p => p.id === id)); }}
                    onDuplicate={(id) => {
                      const p = plans.find(x => x.id === id);
                      if (p) handleDuplicatePlan(p);
                    }}
                    onDelete={(id) => promptDeletePlan(id)}/>
                ))}
              </div>

              {/* Pagination Suite for Plans Grid */}
              <PaginationSuite
                currentPage={planPage}
                totalPages={totalPlanPages}
                totalItems={filteredTemplates.length}
                startIndex={planStartIndex}
                endIndex={planEndIndex}
                pageSize={planPageSize === "all" ? "all" : dynamicPlanPageSize}
                itemLabel="diet templates"
                onPageChange={setPlanPage}
              />

              {filteredTemplates.length === 0 && (
                <div className="text-center py-16">
                  <div className="w-14 h-14 rounded-[18px] flex items-center justify-center mx-auto mb-4"
                    style={{ background:"rgba(107,138,90,0.1)" }}>
                    <FiCalendar size={22} style={{ color:T.olive }}/>
                  </div>
                  <p className="text-[15px] font-bold text-[#1F1F1F]">No template plans found</p>
                  <p className="text-[13px] mt-1" style={{ color:T.muted }}>Create a new 7-day diet template or adjust filters</p>
                </div>
              )}
            </>
          )}

          {viewTab === "user-plans" && (
            <motion.div initial={{ opacity:0, y:12 }} animate={{ opacity:1, y:0 }}>
              {/* User Plans Stat Strip */}
              <div className="ad-kpi grid grid-cols-4 gap-3 mb-5">
                <StatCard icon={FiCalendar} value={userGeneratedPlans.length} label="User Generated Plans"
                  trend="Full 7-day schedules"
                  iconBg={`linear-gradient(135deg,${T.orange},${T.orange2})`}
                  accentColor={`linear-gradient(90deg,${T.orange},${T.orange2})`}
                  cardBg="linear-gradient(145deg,#FFF4E8,#FFF8EE)" delay={0.06}/>
                <StatCard icon={FiUsers} value={new Set(userGeneratedPlans.map(p => p.tier)).size} label="Unique Users"
                  trend="Active customized diets"
                  iconBg={`linear-gradient(135deg,${T.olive},${T.olive2})`}
                  accentColor={`linear-gradient(90deg,${T.olive},${T.olive2})`}
                  cardBg="linear-gradient(145deg,#F5FAF2,#F9FDF5)" delay={0.1}/>
                <StatCard icon={FiZap} value="28 Meals" label="Structure per Plan"
                  trend="4 daily slots × 7 days"
                  iconBg={`linear-gradient(135deg,#534AB7,#7F77DD)`}
                  accentColor="#534AB7" delay={0.14}/>
                <StatCard icon={FiTrendingUp} value={userGeneratedPlans.length > 0 ? `${Math.round(userGeneratedPlans.reduce((a,p) => a + p.totalCal, 0) / userGeneratedPlans.length)} kcal` : "0 kcal"} label="Average Calorie Target"
                  trend="Per user schedule"
                  iconBg={`linear-gradient(135deg,${T.olive},${T.olive2})`}
                  accentColor={T.green} delay={0.18}/>
              </div>

              {/* Filter bar */}
              <div className="ad-filters flex items-center gap-3 mb-5 flex-wrap">
                <div className="ad-filters-full flex items-center gap-2 rounded-[14px] px-3.5 py-2.5"
                  style={{ background:"rgba(255,255,255,0.82)", border:`1px solid ${T.border}` }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#CCC" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                  <input type="text" placeholder="Search user name or ID..." value={userSearch}
                    onChange={e => setUserSearch(e.target.value)}
                    className="border-none outline-none text-[12px] w-52 bg-transparent"
                    style={{ color:T.dark }}/>
                  {userSearch && (
                    <button onClick={() => setUserSearch("")} className="border-none bg-transparent cursor-pointer p-0 text-gray-400 hover:text-gray-600">
                      <FiX size={12}/>
                    </button>
                  )}
                </div>

                <div style={{ width: 170 }}>
                  <CustomSelect value={userDietFilter} onChange={setUserDietFilter}
                    options={[
                      { label:"All Diet Types", value:"all" },
                      { label:"Keto", value:"Keto" },
                      { label:"Veg", value:"Veg" },
                      { label:"Non-Veg", value:"Non-Veg" },
                      { label:"Vegan", value:"Vegan" },
                      { label:"Eggetarian", value:"Eggetarian" },
                      { label:"Jain", value:"Jain" },
                      { label:"Pescatarian", value:"Pescatarian" },
                      { label:"Sattvic", value:"Sattvic" }
                    ]} />
                </div>

                <span className="ad-filters-full text-[11px] ml-auto font-bold" style={{ color:T.muted }}>
                  {filteredUserPlans.length} user plan{filteredUserPlans.length!==1?"s":""}
                </span>
              </div>

              {/* User plans grid */}
              <div className="grid gap-4" style={{ gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))" }}>
                {filteredUserPlans.map((plan, i) => (
                  <PlanCard key={plan.id} plan={plan} delay={i * 0.04}
                    onOpen={setDrawerID}
                    onEdit={(id) => { setEditPlan(plans.find(p => p.id === id)); }}
                    onDuplicate={(id) => {
                      const p = plans.find(x => x.id === id);
                      if (p) handleDuplicatePlan(p);
                    }}
                    onDelete={(id) => promptDeletePlan(id)}/>
                ))}
              </div>

              {filteredUserPlans.length === 0 && (
                <div className="text-center py-16 bg-white/50 rounded-[28px] border border-dashed border-zinc-200">
                  <div className="w-14 h-14 rounded-[18px] flex items-center justify-center mx-auto mb-4"
                    style={{ background:"rgba(242,154,12,0.1)" }}>
                    <FiZap size={22} style={{ color:T.orange }}/>
                  </div>
                  <p className="text-[15px] font-bold text-[#1F1F1F]">No user generated plans found</p>
                  <p className="text-[13px] mt-1" style={{ color:T.muted }}>When users generate personalized 7-day AI plans from their dashboard, they will appear grouped here.</p>
                </div>
              )}
            </motion.div>
          )}

          {viewTab === "tracking" && (
            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}>
              {/* User Tracking Stats strip */}
              <div className="ad-kpi grid grid-cols-5 gap-3 mb-5">
                <StatCard icon={FiActivity} value={trackingStats.activeLoggers} label="Active Loggers"
                  trend="Diet completes logged"
                  iconBg="linear-gradient(135deg, #10B981, #059669)"
                  accentColor="linear-gradient(90deg, #10B981, #059669)"
                  cardBg="linear-gradient(145deg, #E6F8F3, #F0FDF8)" delay={0.06}/>
                <StatCard icon={FiCalendar} value={trackingStats.usersCompletedToday} label="Adherent Today"
                  trend={`${trackingStats.usersPartialToday} partially adherent today`}
                  iconBg={`linear-gradient(135deg,${T.olive},${T.olive2})`}
                  accentColor={`linear-gradient(90deg,${T.olive},${T.olive2})`}
                  cardBg="linear-gradient(145deg,#F5FAF2,#F9FDF5)" delay={0.1}/>
                <StatCard icon={FiTrendingUp} value={trackingStats.usersCompletedWeek} label="Adherent This Week"
                  trend="Users completed all meals"
                  iconBg={`linear-gradient(135deg,${T.olive},${T.olive2})`}
                  accentColor={`linear-gradient(90deg,${T.olive},${T.olive2})`}
                  cardBg="linear-gradient(145deg,#F5FAF2,#F9FDF5)" delay={0.14}/>
                <StatCard icon={FiUsers} value={trackingStats.total} label="Monitored Users"
                  trend="Total tracked customers"
                  iconBg={`linear-gradient(135deg,${T.orange},${T.orange2})`}
                  accentColor={`linear-gradient(90deg,${T.orange},${T.orange2})`}
                  cardBg="linear-gradient(145deg,#FFF4E8,#FFF8EE)" delay={0.18}/>
                <StatCard icon={FiAlertCircle} value={trackingStats.noPlan} label="Missing Plan"
                  trend="No matching admin plan"
                  iconBg="linear-gradient(135deg, #EF4444, #DC2626)"
                  accentColor="linear-gradient(90deg, #EF4444, #DC2626)"
                  cardBg="linear-gradient(145deg, #FEE2E2, #FEF2F2)" delay={0.22}/>
              </div>

              {/* Filters bar */}
              <div className="ad-filters flex items-center gap-3 mb-5 flex-wrap">
                {/* Search bar */}
                <div className="ad-filters-full flex items-center gap-2 rounded-[14px] px-3.5 py-2.5"
                  style={{ background:"rgba(255,255,255,0.82)", border:`1px solid ${T.border}` }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#CCC" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                  <input value={trackingSearch} onChange={e => setTrackingSearch(e.target.value)}
                    placeholder="Search users, plans…"
                    className="bg-transparent border-none outline-none text-[13px] text-[#1F1F1F]"
                    style={{ fontFamily:"'Inter',sans-serif", width:180 }}/>
                </div>

                {/* Tier selector */}
                <div style={{ width: 160 }}>
                  <CustomSelect value={trackingTierF} onChange={setTrackingTierF}
                    options={[
                      { label: "All Tiers", value: "all" },
                      { label: "Free Tier", value: "Free" },
                      { label: "Premium Tier", value: "Premium" },
                      { label: "YTT Pro Tier", value: "YTT Pro" }
                    ]} />
                </div>

                {/* Progress status selector */}
                <div style={{ width: 180 }}>
                  <CustomSelect value={trackingStatusF} onChange={setTrackingStatusF}
                    options={[
                      { label: "All Statuses", value: "all" },
                      { label: "Completed (100%)", value: "completed" },
                      { label: "In Progress", value: "partial" },
                      { label: "Not Started (0%)", value: "not_started" }
                    ]} />
                </div>

                {/* Page size selector */}
                <div style={{ width: 160 }}>
                  <CustomSelect value={trackingPageSize} onChange={val => {
                    setTrackingPageSize(val === "all" || val === "auto" ? val : Number(val));
                    setTrackingPage(1);
                  }}
                    options={trackingPageSizeOptions} />
                </div>

                {/* Reload button */}
                <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                  onClick={loadUserTracking} disabled={loadingTracking}
                  className="w-10 h-10 rounded-[14px] flex items-center justify-center cursor-pointer border"
                  style={{ background: "#fff", borderColor: T.border, color: T.muted }}
                  title="Reload Tracking Data">
                  <FiRefreshCw size={14} className={loadingTracking ? "animate-spin" : ""} />
                </motion.button>

                <span className="ad-filters-full text-[11px] ml-auto font-bold" style={{ color:T.muted }}>
                  {filteredTracking.length} user{filteredTracking.length!==1?"s":""} found
                </span>
              </div>

              {/* Progress Table */}
              <div className="ad-pad bg-[rgba(255,255,255,0.7)] backdrop-blur-md rounded-[24px] border overflow-hidden p-6 shadow-sm"
                style={{ borderColor: T.border }}>
                <div className="ad-table overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b" style={{ borderColor: "rgba(239,229,216,0.5)" }}>
                        <th className="pb-3 text-[11px] font-black uppercase tracking-wider" style={{ color: T.muted }}>User Info</th>
                        <th className="pb-3 text-[11px] font-black uppercase tracking-wider" style={{ color: T.muted }}>Preferences</th>
                        <th className="pb-3 text-[11px] font-black uppercase tracking-wider" style={{ color: T.muted }}>Assigned Diet Plan</th>
                        <th className="pb-3 text-[11px] font-black uppercase tracking-wider" style={{ color: T.muted }}>Today's Diet</th>
                        <th className="pb-3 text-[11px] font-black uppercase tracking-wider" style={{ color: T.muted }}>Weekly Progress</th>
                        <th className="pb-3 text-[11px] font-black uppercase tracking-wider" style={{ color: T.muted }}>Calorie Status</th>
                        <th className="pb-3 text-[11px] font-black uppercase tracking-wider" style={{ color: T.muted }}>Water Goal</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[rgba(239,229,216,0.3)]">
                      {paginatedTracking.map(item => {
                        const hasNoPlan = item.matchedPlanName === "No Published Plan" || item.totalMeals === 0;
                        const pct = item.totalMeals > 0 ? Math.round((item.completedMeals / item.totalMeals) * 100) : 0;
                        
                        return (
                          <tr key={item.userId} className="hover:bg-[rgba(107,138,90,0.02)] transition-all duration-150">
                            {/* User details */}
                            <td className="py-4 pr-3">
                              <p className="font-bold text-[13px] text-[#1F1F1F]">{item.fullName}</p>
                              <p className="text-[11px]" style={{ color: T.muted }}>{item.email}</p>
                              <span className="inline-block text-[9px] font-black px-2 py-0.5 rounded-full mt-1.5"
                                style={{
                                  background: item.tier === "Free" ? "rgba(120,120,120,0.1)" : item.tier === "Premium" ? "rgba(242,154,12,0.1)" : "rgba(107,138,90,0.1)",
                                  color: item.tier === "Free" ? T.muted : item.tier === "Premium" ? T.orange : T.olive
                                }}>
                                {item.tier}
                              </span>
                            </td>
                            {/* Preferences */}
                            <td className="py-4 pr-3">
                              <div className="flex flex-col gap-1">
                                <span className="inline-flex items-center gap-1 w-fit text-[10px] font-bold px-2 py-0.5 rounded-[6px]"
                                  style={{ background: "rgba(107,138,90,0.06)", color: T.olive }}>
                                  <FiLayers size={10} /> {item.dietType}
                                </span>
                                <span className="text-[11px] flex items-center gap-1" style={{ color: T.muted }}>
                                  <FiGlobe size={11} /> {item.region}
                                </span>
                                <span className="text-[11px] flex items-center gap-1" style={{ color: T.muted }}>
                                  <FiZap size={11} /> {item.calorieGoal}
                                </span>
                              </div>
                            </td>
                            {/* Diet plan match status */}
                            <td className="py-4 pr-3">
                              {hasNoPlan ? (
                                <div className="flex items-center gap-1 text-[#E24B4A]">
                                  <FiAlertCircle size={13} />
                                  <span className="text-[12px] font-black">No Published Plan</span>
                                </div>
                              ) : (
                                <div>
                                  <p className="font-bold text-[12px] text-[#1F1F1F]">{item.matchedPlanName}</p>
                                  <p className="text-[10px]" style={{ color: T.muted }}>Updated {new Date(item.updatedAt).toLocaleDateString()}</p>
                                </div>
                              )}
                            </td>
                            {/* Today's Diet Completion Status */}
                            <td className="py-4 pr-3">
                              {hasNoPlan ? (
                                <span className="text-[11px]" style={{ color: T.muted }}>-</span>
                              ) : item.totalMealsToday === 0 ? (
                                <span className="text-[11px] italic" style={{ color: T.muted }}>No meals today</span>
                              ) : (
                                <div>
                                  {item.completedMealsToday === item.totalMealsToday ? (
                                    <span className="inline-flex items-center gap-1.5 text-[11px] font-black px-2.5 py-1 rounded-full text-emerald-700 bg-emerald-50 border border-emerald-200">
                                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                                      Complete ({item.completedMealsToday}/{item.totalMealsToday})
                                    </span>
                                  ) : item.completedMealsToday > 0 ? (
                                    <span className="inline-flex items-center gap-1.5 text-[11px] font-black px-2.5 py-1 rounded-full text-amber-700 bg-amber-50 border border-amber-200">
                                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span>
                                      In Progress ({item.completedMealsToday}/{item.totalMealsToday})
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full text-gray-500 bg-gray-50 border border-gray-200">
                                      Not Started (0/{item.totalMealsToday})
                                    </span>
                                  )}
                                </div>
                              )}
                            </td>
                            {/* Weekly progress bar */}
                            <td className="py-4 pr-3">
                              {hasNoPlan ? (
                                <span className="text-[11px]" style={{ color: T.muted }}>-</span>
                              ) : (
                                <div className="w-[130px]">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-[11px] font-bold text-[#1F1F1F]">
                                      {item.completedMeals} / {item.totalMeals} meals
                                    </span>
                                    <span className="text-[10px] font-black" style={{ color: pct === 100 ? "#10B981" : T.olive }}>
                                      {pct}%
                                    </span>
                                  </div>
                                  <div className="w-full h-1.5 rounded-full bg-gray-100 overflow-hidden">
                                    <div className="h-full rounded-full transition-all duration-500"
                                      style={{
                                        width: `${pct}%`,
                                        background: pct === 100 ? "linear-gradient(90deg,#10B981,#059669)" : `linear-gradient(90deg,${T.olive},${T.olive2})`
                                      }}
                                    />
                                  </div>
                                </div>
                              )}
                            </td>
                            {/* Calories consumed vs target */}
                            <td className="py-4 pr-3">
                              {hasNoPlan ? (
                                <span className="text-[11px]" style={{ color: T.muted }}>-</span>
                              ) : (
                                <div>
                                  <p className="font-bold text-[12px] text-[#1F1F1F]">{item.caloriesConsumed} / {item.targetCalories} kcal</p>
                                  <p className="text-[10px]" style={{ color: T.muted }}>
                                    {item.caloriesConsumed >= item.targetCalories ? (
                                      <span className="inline-flex items-center gap-1 text-[#10B981] font-bold">
                                        <FiCheckCircle size={11} /> Target met
                                      </span>
                                    ) : (
                                      "Active logging"
                                    )}
                                  </p>
                                </div>
                              )}
                            </td>
                            {/* Water Intake Tracker */}
                            <td className="py-4">
                              <div className="flex items-center gap-1">
                                <span className="text-[12px] font-bold text-blue-600 flex items-center gap-1">
                                  <FiDroplet size={12} className="text-blue-500" />
                                  {item.waterIntakeGss === 4 ? 0 : item.waterIntakeGss} glasses
                                </span>
                                <span className="text-[10px]" style={{ color: T.muted }}>(target)</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}

                      {filteredTracking.length === 0 && (
                        <tr>
                          <td colSpan="7" className="text-center py-12">
                            <div className="w-12 h-12 rounded-[16px] flex items-center justify-center mx-auto mb-3"
                              style={{ background: "rgba(107,138,90,0.06)" }}>
                              <FiUsers size={20} style={{ color: T.olive }} />
                            </div>
                            <p className="text-[13px] font-bold text-[#1F1F1F]">No users found matching filters</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Suite for User Tracking */}
                <PaginationSuite
                  currentPage={trackingPage}
                  totalPages={totalTrackingPages}
                  totalItems={filteredTracking.length}
                  pageSize={trackingPageSize === "all" ? "all" : dynamicTrackingPageSize}
                  itemLabel="tracked users"
                  onPageChange={setTrackingPage}
                />
              </div>
            </motion.div>
          )}
        </div>

        {/* Plan editor drawer */}
        <AnimatePresence>
          {drawerPlan && (
            <PlanDrawer plan={drawerPlan} onClose={() => setDrawerID(null)} showToast={showToast}
              onEdit={(p) => { setEditPlan(p); }}
              onDuplicate={(p) => handleDuplicatePlan(p)}
              onToggleStatus={(p) => handleToggleStatus(p)}
              onDelete={(id) => promptDeletePlan(id)}/>
          )}
        </AnimatePresence>

        {/* Create / Edit modal */}
        <AnimatePresence>
          {(showCreate || editPlan) && (
            <PlanModal plan={editPlan}
              onClose={() => { setShowCreate(false); setEditPlan(null); }}
              onSaveSuccess={loadAdminPlans}
              showToast={showToast}/>
          )}
        </AnimatePresence>

        {/* Custom Confirm Modal (No default window.confirm or alert) */}
        <AnimatePresence>
          {confirmModal.isOpen && (
            <ConfirmModal
              isOpen={confirmModal.isOpen}
              title={confirmModal.title}
              message={confirmModal.message}
              confirmText={confirmModal.confirmText}
              isDanger={confirmModal.isDanger}
              onConfirm={confirmModal.onConfirm}
              onCancel={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}/>
          )}
        </AnimatePresence>

        {/* Toast */}
        <AnimatePresence>
          {toast && (
            <motion.div key={toast}
              initial={{ opacity:0,y:16,scale:0.95 }} animate={{ opacity:1,y:0,scale:1 }}
              exit={{ opacity:0,y:16 }}
              className="ad-toast fixed bottom-7 right-7 z-[300] flex items-center gap-3 px-5 py-3.5 rounded-[16px] text-white text-[13px] font-semibold"
              style={{ background:"linear-gradient(135deg,#1F1F1F,#2D2D2D)",
                boxShadow:"0 16px 48px rgba(0,0,0,0.25)", fontFamily:"'Inter',sans-serif" }}>
              <FiCheckCircle size={16} style={{ color:T.olive }}/>{toast}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </AdminDashboardLayout>
  );
}